/**
 * UUHP Demo Flow Components — bundled small/medium components for Flows 1, 3, 4, 5, 6, 7.
 *
 *   <CampaignApprovalControls />        Flow 1 — request + faux-admin-approve
 *   <LanguageVariantTabs />             Flow 1 — used inside campaign step editor
 *   <JourneyStepLanguageGrid />         Flow 1 — side-by-side multi-language editor
 *   <JourneyPreviewModal />             Flow 1 — multi-language preview
 *   <JourneyStepDragDrop />             Flow 1 — drag-and-drop journey step order
 *   <UUHPJourneyBuilderPage />          Flow 1 — full builder page (drag + lang + approve + preview)
 *   <CampaignDimensionBreakdown />      Flow 3 — by-language / by-channel grid
 *   <ExportMenuButton />                Flow 3 — XLSX / PDF / CSV export
 *   <ScheduleReportModal />             Flow 3 — weekly auto-report
 *   <UUHPCampaignProgressPage />        Flow 3 — full campaign-progress dashboard
 *   <TemplateCloneWizard />             Flow 4 — clone + segment-suppress flow
 *   <UUHPDataIngestionPanel />          Flow 5 — SFTP last job + Poll-now
 *   <AuditExportButton />               Flow 5 — date-range audit dump
 *   <ReferralAutomationRuleEditor />    Flow 6 — list/create rules
 *   <AgentSessionDetail />              Flow 6 — state timeline + LLM decisions
 *   <SimulateInboundSMSButton />        Flow 2 — stage an inbound SMS through real classifier
 *   <IncentiveCompletionTrigger />      Flow 7 — admin-triggered earning lifecycle
 */
import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { authFetch, authFetchJson } from '../../lib/authFetch';
import { useTabbedChrome } from '@/components/weavein';

// The SFTP report needs the auth + X-CBO-ID headers (TRUS-639), which a plain
// <a href> navigation doesn't send, so fetch it and save the blob.
async function downloadSftpReport(jobId: number | string) {
  const res = await authFetch(`/api/integrations/sftp/jobs/${jobId}/report`);
  if (!res.ok) return;
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `sftp_job_${jobId}_report.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ============================================================================
// Flow 1 — Approval controls
// ============================================================================
export const CampaignApprovalControls: React.FC<{
  campaignId: string;
  approvalStatus: 'not_required' | 'pending_approval' | 'approved' | 'rejected';
  onChange?: () => void;
}> = ({ campaignId, approvalStatus, onChange }) => {
  const [busy, setBusy] = useState(false);
  const request = async () => {
    setBusy(true);
    try {
      await authFetch(`/api/campaigns/${campaignId}/request-approval`, {
        method: 'POST', jsonBody: {},
      });
      onChange?.();
    } finally { setBusy(false); }
  };
  const decide = async (decision: 'approved' | 'rejected') => {
    setBusy(true);
    try {
      await authFetch(`/api/campaigns/${campaignId}/approval-decision`, {
        method: 'POST', jsonBody: { decision },
      });
      onChange?.();
    } finally { setBusy(false); }
  };

  if (approvalStatus === 'not_required') {
    return (
      <button disabled={busy} onClick={request}
        className="rounded bg-v2accent px-3 py-1.5 text-sm text-white hover:bg-v2accent/90 disabled:opacity-50">
        Request approval
      </button>
    );
  }
  if (approvalStatus === 'pending_approval') {
    return (
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-bold uppercase text-yellow-700">
          pending approval
        </span>
        <button disabled={busy} onClick={() => decide('approved')}
          className="rounded bg-v2accent px-2 py-1 text-xs text-white hover:bg-v2accent/90">
          Approve (admin)
        </button>
        <button disabled={busy} onClick={() => decide('rejected')}
          className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700">
          Reject
        </button>
      </div>
    );
  }
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-bold uppercase ${
      approvalStatus === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
    }`}>
      {approvalStatus}
    </span>
  );
};

// ============================================================================
// Flow 1 — LanguageVariantTabs (reusable inside step editor)
// ============================================================================
export const LANG_FLAG: Record<string, string> = {
  en: '🇺🇸', es: '🇲🇽', vi: '🇻🇳', zh: '🇨🇳', ar: '🇸🇦', ko: '🇰🇷',
};

export const LanguageVariantTabs: React.FC<{
  campaignId: string;
  campaignEngagementId: string;
  enabledLanguages: string[];
}> = ({ campaignId, campaignEngagementId, enabledLanguages }) => {
  const [variants, setVariants] = useState<Record<string, { content: string; subject: string | null; auto_translated: boolean }>>({});
  const [active, setActive] = useState<string>(enabledLanguages[0] || 'en');
  const [draft, setDraft] = useState<{ content: string; subject: string }>({ content: '', subject: '' });

  const refresh = async () => {
    const list = await authFetchJson<Array<{ language: string; content: string; subject: string | null; auto_translated: boolean }>>(
      `/api/campaigns/${campaignId}/steps/${campaignEngagementId}/language-variants`,
    );
    const map: Record<string, any> = {};
    for (const v of list) map[v.language] = v;
    setVariants(map);
  };
  useEffect(() => { refresh(); }, [campaignId, campaignEngagementId]);
  useEffect(() => {
    const v = variants[active];
    setDraft({ content: v?.content || '', subject: v?.subject || '' });
  }, [active, variants]);

  const save = async () => {
    await authFetch(`/api/campaigns/${campaignId}/steps/${campaignEngagementId}/language-variants/${active}`, {
      method: 'PUT', jsonBody: { content: draft.content, subject: draft.subject || null },
    });
    refresh();
  };

  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <div className="mb-3 flex flex-wrap gap-1">
        {enabledLanguages.map(lang => (
          <button key={lang} onClick={() => setActive(lang)}
            className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs ${
              active === lang ? 'bg-v2accent text-white' : 'border border-v2border bg-surface text-ink-500 hover:text-ink'
            }`}>
            <span>{LANG_FLAG[lang] || '🌐'}</span><span>{lang.toUpperCase()}</span>
            {variants[lang]?.auto_translated && <span className="text-[9px] opacity-75">auto</span>}
          </button>
        ))}
      </div>
      <textarea
        value={draft.content}
        onChange={e => setDraft(d => ({ ...d, content: e.target.value }))}
        rows={4}
        className="w-full rounded border border-gray-200 px-2 py-1 text-sm"
        placeholder={`Content for ${active}…`}
      />
      <input
        value={draft.subject}
        onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))}
        className="mt-2 w-full rounded border border-gray-200 px-2 py-1 text-sm"
        placeholder="Email subject (email steps only)"
      />
      <button onClick={save}
        className="mt-2 rounded bg-v2accent px-3 py-1 text-xs text-white hover:bg-v2accent/90">
        Save override
      </button>
    </div>
  );
};

// ============================================================================
// Flow 1 — JourneyPreviewModal
// ============================================================================
export const JourneyPreviewModal: React.FC<{
  campaignId: string;
  enabledLanguages: string[];
  onClose: () => void;
}> = ({ campaignId, enabledLanguages, onClose }) => {
  const [steps, setSteps] = useState<any[]>([]);
  useEffect(() => {
    authFetch(`/api/campaigns/${campaignId}/preview`, {
      method: 'POST', jsonBody: { languages: enabledLanguages },
    }).then(r => r.json()).then(d => setSteps(d.steps || []));
  }, [campaignId, enabledLanguages.join(',')]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-auto rounded-lg bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Journey preview</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">✕</button>
        </div>
        {steps.map((s, i) => (
          <div key={s.campaign_engagement_id} className="mb-4 rounded-lg border border-gray-200 p-3">
            <div className="mb-2 text-xs font-bold uppercase text-blue-600">
              Step {s.engagement_order} · {s.engagement_type}
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {Object.entries(s.languages).map(([lang, payload]: [string, any]) => (
                <div key={lang} className="rounded border border-gray-100 bg-gray-50 p-2">
                  <div className="mb-1 text-xs font-bold">
                    {LANG_FLAG[lang] || '🌐'} {lang.toUpperCase()}
                  </div>
                  {payload.subject && (
                    <div className="mb-1 text-xs italic text-gray-500">Subj: {payload.subject}</div>
                  )}
                  <div className="text-xs text-gray-800">{payload.content}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Flow 3 — CampaignDimensionBreakdown
// ============================================================================
export const CampaignDimensionBreakdown: React.FC<{
  cboId: number;
  campaignId: string;
  dim: 'language' | 'channel' | 'template';
  window?: string;
}> = ({ cboId, campaignId, dim, window = '7d' }) => {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => {
    authFetchJson<any>(
      `/api/campaigns/${campaignId}/analytics?cbo_id=${cboId}&dim=${dim}&window=${window}`,
    ).then(d => setRows(d.breakdown || []));
  }, [cboId, campaignId, dim, window]);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-left text-xs font-bold uppercase text-gray-500">
            <th className="px-3 py-2">{dim}</th>
            <th className="px-3 py-2">Sent</th>
            <th className="px-3 py-2">Delivery</th>
            <th className="px-3 py-2">Response</th>
            <th className="px-3 py-2">Opt-out</th>
            <th className="px-3 py-2">Bounce</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="px-3 py-2 font-medium">{r.key ?? '—'}</td>
              <td className="px-3 py-2">{r.sent}</td>
              <td className="px-3 py-2">{(r.delivery_rate * 100).toFixed(1)}%</td>
              <td className="px-3 py-2">{(r.response_rate * 100).toFixed(1)}%</td>
              <td className="px-3 py-2">{(r.opt_out_rate * 100).toFixed(1)}%</td>
              <td className="px-3 py-2">{(r.bounce_rate * 100).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// Flow 3 — Export menu
// ============================================================================
export const ExportMenuButton: React.FC<{
  cboId: number;
  campaignId: string;
  window?: string;
}> = ({ cboId, campaignId, window = '7d' }) => {
  const [open, setOpen] = useState(false);
  const trigger = async (format: 'xlsx' | 'pdf' | 'csv', scope: 'summary' | 'events') => {
    setOpen(false);
    const res = await authFetch(
      `/api/campaigns/${campaignId}/export?cbo_id=${cboId}&window=${window}`,
      { method: 'POST', jsonBody: { format, scope } },
    );
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaign_${campaignId.slice(0, 8)}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="relative inline-block">
      <button onClick={() => setOpen(o => !o)}
        className="rounded bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200">
        Export ▾
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-44 rounded-lg border border-gray-200 bg-white p-1 shadow-lg">
          <button onClick={() => trigger('xlsx', 'summary')}
            className="block w-full rounded px-3 py-1.5 text-left text-sm hover:bg-gray-50">XLSX summary</button>
          <button onClick={() => trigger('pdf', 'summary')}
            className="block w-full rounded px-3 py-1.5 text-left text-sm hover:bg-gray-50">PDF summary</button>
          <button onClick={() => trigger('csv', 'events')}
            className="block w-full rounded px-3 py-1.5 text-left text-sm hover:bg-gray-50">CSV (raw events)</button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Flow 3 — ScheduleReportModal
// ============================================================================
export const ScheduleReportModal: React.FC<{
  cboId: number;
  defaultName?: string;
  onClose: () => void;
  onCreated?: () => void;
}> = ({ cboId, defaultName = 'Weekly Campaign Recap', onClose, onCreated }) => {
  const [name, setName] = useState(defaultName);
  const [emails, setEmails] = useState('');
  const [format, setFormat] = useState<'xlsx' | 'pdf' | 'csv'>('xlsx');
  const [day, setDay] = useState(1);
  const [hour, setHour] = useState(8);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await authFetch('/api/reports/scheduled', {
        method: 'POST',
        jsonBody: {
          cbo_id: cboId, name, frequency: 'weekly', day_of_week: day, hour_of_day: hour,
          format, delivery_method: 'email',
          delivery_config: { emails: emails.split(',').map(s => s.trim()).filter(Boolean) },
          report_type: 'campaign_overview',
        },
      });
      onCreated?.();
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5">
        <h2 className="mb-4 text-base font-semibold">Schedule weekly report</h2>
        <div className="space-y-3">
          <input value={name} onChange={e => setName(e.target.value)}
            className="w-full rounded border border-gray-200 px-3 py-2 text-sm" placeholder="Report name" />
          <div className="grid grid-cols-2 gap-2">
            <select value={day} onChange={e => setDay(Number(e.target.value))}
              className="rounded border border-gray-200 px-2 py-2 text-sm">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) =>
                <option key={i} value={i}>{d}</option>)}
            </select>
            <input type="number" value={hour} onChange={e => setHour(Number(e.target.value))}
              min={0} max={23}
              className="rounded border border-gray-200 px-2 py-2 text-sm" placeholder="Hour" />
          </div>
          <select value={format} onChange={e => setFormat(e.target.value as any)}
            className="w-full rounded border border-gray-200 px-2 py-2 text-sm">
            <option value="xlsx">XLSX</option>
            <option value="pdf">PDF</option>
            <option value="csv">CSV</option>
          </select>
          <input value={emails} onChange={e => setEmails(e.target.value)}
            className="w-full rounded border border-gray-200 px-3 py-2 text-sm"
            placeholder="Recipient emails (comma-separated)" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded bg-gray-100 px-3 py-1.5 text-sm">Cancel</button>
          <button disabled={busy} onClick={submit}
            className="rounded bg-v2accent px-3 py-1.5 text-sm text-white hover:bg-v2accent/90">
            {busy ? 'Saving…' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Flow 4 — TemplateCloneWizard with segment-suppress
// ============================================================================
export const TemplateCloneWizard: React.FC<{
  cboId: number;
  userId: number;
  templateId: number;
  measureId: number | null;
  groupId: string;
  onClose: () => void;
  onLaunched?: (campaignId: string) => void;
}> = ({ cboId, userId, templateId, measureId, groupId, onClose, onLaunched }) => {
  const [suppress, setSuppress] = useState(true);
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!groupId) return;
    authFetch(`/api/campaigns/preview/segment-preview`, {
      method: 'POST',
      jsonBody: { cbo_id: cboId, group_id: groupId,
                  suppress_measure_completed: suppress, measure_id: measureId },
    }).then(r => r.json()).then(setPreview).catch(() => setPreview(null));
  }, [groupId, suppress, measureId, cboId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5">
        <h2 className="mb-4 text-base font-semibold">Clone & customize</h2>
        <label className="mb-3 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={suppress} onChange={e => setSuppress(e.target.checked)} />
          Suppress members who already completed this measure
        </label>
        {preview && (
          <div className="mb-3 rounded bg-gray-50 p-3 text-sm">
            <div>Recipients after suppression: <strong>{preview.recipient_count}</strong> of {preview.base_count}</div>
            <div className="text-xs text-gray-500">Suppressed: {preview.suppressed_count}</div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded bg-gray-100 px-3 py-1.5 text-sm">Cancel</button>
          <button disabled={busy} className="rounded bg-v2accent px-3 py-1.5 text-sm text-white"
            onClick={async () => {
              setBusy(true);
              try {
                const res = await authFetch(`/api/templates/${templateId}/clone`, {
                  method: 'POST', jsonBody: { cbo_id: cboId, user_id: userId },
                });
                const j = (await res.json()) as { new_template_id: number };
                onLaunched?.(`tpl-${j.new_template_id}`);
                onClose();
              } finally { setBusy(false); }
            }}>
            {busy ? 'Cloning…' : 'Clone & open'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Flow 5 — UUHPDataIngestionPanel
// ============================================================================
interface IngestRow {
  row_number: number;
  validation_status: 'VALID' | 'WARN' | 'ERROR';
  action_taken: string | null;
  validation_errors: Array<{ field?: string; code?: string; message?: string }>;
  raw_row: Record<string, any>;
  member_id_resolved: number | null;
  created_at: string | null;
}
interface IngestRowsPayload {
  job_id: number;
  total: number;
  page: number;
  page_size: number;
  summary: { valid?: number; warn?: number; error?: number; inserted?: number; updated?: number; skipped?: number; total?: number };
  rows: IngestRow[];
}

export const UUHPDataIngestionPanel: React.FC<{ cboId: number }> = ({ cboId }) => {
  const [conns, setConns] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [reviewJob, setReviewJob] = useState<any | null>(null);
  const [reviewData, setReviewData] = useState<IngestRowsPayload | null>(null);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'VALID' | 'WARN' | 'ERROR'>('all');
  const [reviewLoading, setReviewLoading] = useState(false);

  const refresh = async () => {
    const [c, j] = await Promise.all([
      authFetchJson<any[]>(`/api/integrations/sftp/connections?cbo_id=${cboId}`),
      authFetchJson<any[]>(`/api/integrations/sftp/jobs?cbo_id=${cboId}`),
    ]);
    setConns(c); setJobs(j);
  };
  useEffect(() => { refresh(); }, [cboId]);

  const pollNow = async (id: number) => {
    setBusy(true);
    try {
      await authFetch(`/api/integrations/sftp/connections/${id}/poll-now`, { method: 'POST' });
      await refresh();
    } finally { setBusy(false); }
  };

  const loadReview = async (job: any, filter: typeof reviewFilter) => {
    setReviewLoading(true);
    try {
      const qs = filter === 'all' ? '' : `&status=${filter}`;
      const data = await authFetchJson<IngestRowsPayload>(
        `/api/integrations/sftp/jobs/${job.id}/rows?page=1&page_size=200${qs}`,
      );
      setReviewData(data);
    } catch (err) {
      console.error(err); setReviewData(null);
    } finally { setReviewLoading(false); }
  };

  const openReview = (job: any) => {
    setReviewJob(job); setReviewFilter('all'); setReviewData(null);
    loadReview(job, 'all');
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">SFTP connections</h3>
        {conns.map(c => (
          <div key={c.id} className="mb-2 flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div className="text-sm font-medium">{c.name}</div>
            <div className="text-xs text-gray-500">{c.host}:{c.port}{c.remote_dir}</div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-gray-500">
                {c.last_status} · {c.last_polled_at ? new Date(c.last_polled_at).toLocaleString() : 'never'}
              </span>
              <button disabled={busy} onClick={() => pollNow(c.id)}
                className="rounded bg-v2accent px-2 py-1 text-xs text-white hover:bg-v2accent/90 disabled:opacity-50">
                Poll now
              </button>
            </div>
          </div>
        ))}
      </div>
      <div>
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">Recent ingest jobs</h3>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr><th className="px-3 py-2">Started</th><th className="px-3 py-2">File</th>
                <th className="px-3 py-2">Status</th><th className="px-3 py-2">Total</th>
                <th className="px-3 py-2">Valid / Invalid</th>
                <th className="px-3 py-2">Inserted / Updated / Skipped</th>
                <th className="px-3 py-2">Report</th></tr>
          </thead>
          <tbody>
            {jobs.map(j => {
              const reconciled = (j.valid_rows ?? 0) + (j.invalid_rows ?? 0) === (j.total_rows ?? 0);
              return (
              <tr key={j.id} className="border-b border-gray-100">
                <td className="px-3 py-2">{new Date(j.started_at).toLocaleString()}</td>
                <td className="px-3 py-2">{j.filename}</td>
                <td className="px-3 py-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${
                    j.status === 'COMPLETED' ? 'bg-green-100 text-green-700'
                      : j.status === 'FAILED' ? 'bg-red-100 text-red-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>{j.status}</span>
                </td>
                <td className="px-3 py-2">{j.total_rows}</td>
                <td className="px-3 py-2">
                  <span className={!reconciled ? 'text-red-700 font-semibold' : ''}>
                    {j.valid_rows} / {j.invalid_rows}
                  </span>
                  {!reconciled && (
                    <span className="ml-1 text-[10px] text-red-600" title="valid + invalid ≠ total — counts may be stale">⚠</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-gray-700">
                  {j.inserted_rows ?? 0} ins · {j.updated_rows ?? 0} upd · {j.skipped_rows ?? 0} skip
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button
                    onClick={() => openReview(j)}
                    className="text-blue-600 hover:underline mr-3"
                  >
                    Review rows
                  </button>
                  <button type="button" onClick={() => downloadSftpReport(j.id)}
                     className="text-blue-600 hover:underline">Download CSV</button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {/* Review-rows modal — opens a scrollable table of every row in the
          ingest job (VALID / WARN / ERROR), with a status filter. The
          same data backs the CSV download. */}
      {reviewJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-5xl max-h-[90vh] rounded-lg bg-white shadow-xl flex flex-col">
            <div className="flex items-start justify-between border-b border-gray-200 px-5 py-3">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Ingest job · {reviewJob.filename}</h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  {new Date(reviewJob.started_at).toLocaleString()} · job #{reviewJob.id}
                </p>
              </div>
              <button
                onClick={() => { setReviewJob(null); setReviewData(null); }}
                className="rounded p-1 text-gray-400 hover:bg-gray-100"
              >✕</button>
            </div>
            <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-2.5 text-xs">
              <span className="font-semibold text-gray-700">Filter:</span>
              {(['all', 'VALID', 'WARN', 'ERROR'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => { setReviewFilter(f); loadReview(reviewJob, f); }}
                  className={`rounded-full px-2.5 py-0.5 transition ${
                    reviewFilter === f
                      ? 'bg-v2accent text-white'
                      : 'border border-v2border bg-surface text-ink-500 hover:text-ink'
                  }`}
                >
                  {f === 'all' ? 'All' : f}
                </button>
              ))}
              {reviewData?.summary && (
                <span className="ml-auto text-gray-600">
                  <span className="text-green-700 font-semibold">{reviewData.summary.valid ?? 0} valid</span>
                  {' · '}
                  <span className="text-amber-700 font-semibold">{reviewData.summary.warn ?? 0} warn</span>
                  {' · '}
                  <span className="text-red-700 font-semibold">{reviewData.summary.error ?? 0} error</span>
                  {' · '}
                  {reviewData.summary.inserted ?? 0} ins / {reviewData.summary.updated ?? 0} upd / {reviewData.summary.skipped ?? 0} skip
                </span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {reviewLoading && (
                <p className="text-center text-sm text-gray-500 py-10">Loading rows…</p>
              )}
              {!reviewLoading && (!reviewData || reviewData.rows.length === 0) && (
                <p className="text-center text-sm text-gray-500 py-10">
                  No rows match this filter.
                </p>
              )}
              {!reviewLoading && reviewData && reviewData.rows.length > 0 && (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 text-left text-[10px] uppercase text-gray-500">
                    <tr>
                      <th className="px-2 py-1.5 w-12">#</th>
                      <th className="px-2 py-1.5 w-20">Status</th>
                      <th className="px-2 py-1.5 w-20">Action</th>
                      <th className="px-2 py-1.5">Member</th>
                      <th className="px-2 py-1.5">External ID</th>
                      <th className="px-2 py-1.5">DOB</th>
                      <th className="px-2 py-1.5">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewData.rows.map(r => (
                      <tr key={r.row_number}
                        className={`border-t border-gray-100 ${
                          r.validation_status === 'ERROR' ? 'bg-red-50' :
                          r.validation_status === 'WARN' ? 'bg-amber-50' : ''
                        }`}
                      >
                        <td className="px-2 py-1.5 font-mono text-gray-500">{r.row_number}</td>
                        <td className="px-2 py-1.5">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                            r.validation_status === 'VALID' ? 'bg-green-100 text-green-800' :
                            r.validation_status === 'WARN'  ? 'bg-amber-100 text-amber-900' :
                            'bg-red-100 text-red-800'
                          }`}>{r.validation_status}</span>
                        </td>
                        <td className="px-2 py-1.5 text-gray-700">{r.action_taken || '—'}</td>
                        <td className="px-2 py-1.5 text-gray-900">
                          {r.raw_row.first_name} {r.raw_row.last_name}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-gray-600">{r.raw_row.member_id_external}</td>
                        <td className="px-2 py-1.5 text-gray-600">{r.raw_row.dob || '—'}</td>
                        <td className="px-2 py-1.5 text-red-700">
                          {r.validation_errors && r.validation_errors.length > 0
                            ? r.validation_errors.map((e, i) => (
                                <div key={i}>
                                  <span className="font-mono text-[10px]">{e.field}:</span> {e.message}
                                </div>
                              ))
                            : <span className="text-gray-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-gray-200 px-5 py-2.5 text-xs">
              {reviewData && (
                <span className="text-gray-600">
                  Showing {reviewData.rows.length} of {reviewData.total} rows
                </span>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => downloadSftpReport(reviewJob.id)}
                  className="rounded border border-v2border bg-surface px-3 py-1 text-ink hover:bg-paper-2"
                >Download full CSV</button>
                <button
                  onClick={() => { setReviewJob(null); setReviewData(null); }}
                  className="rounded bg-gray-100 px-3 py-1 text-gray-700 hover:bg-gray-200"
                >Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Flow 5 — AuditExportButton
// ============================================================================
export const AuditExportButton: React.FC<{ cboId: number }> = ({ cboId }) => {
  const today = new Date().toISOString().slice(0, 10);
  const sevenAgo = new Date(Date.now() - 7 * 86_400_000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(sevenAgo);
  const [to, setTo] = useState(today);
  const [format, setFormat] = useState<'csv' | 'json'>('csv');

  const trigger = async () => {
    const url = `/api/audit/export?cbo_id=${cboId}&from=${from}&to=${to}&format=${format}`;
    const res = await authFetch(url);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `audit_${from}_${to}.${format}`;
    a.click();
  };

  return (
    <div className="inline-flex items-center gap-2">
      <input type="date" value={from} onChange={e => setFrom(e.target.value)}
        className="rounded border border-gray-200 px-2 py-1 text-sm" />
      <span>→</span>
      <input type="date" value={to} onChange={e => setTo(e.target.value)}
        className="rounded border border-gray-200 px-2 py-1 text-sm" />
      <select value={format} onChange={e => setFormat(e.target.value as any)}
        className="rounded border border-gray-200 px-2 py-1 text-sm">
        <option value="csv">CSV</option><option value="json">JSON</option>
      </select>
      <button onClick={trigger}
        className="rounded bg-v2accent px-3 py-1.5 text-sm text-white hover:bg-v2accent/90">
        Export audit
      </button>
    </div>
  );
};

// ============================================================================
// Flow 6 — ReferralAutomationRuleEditor (with Create/Edit/Delete)
// ============================================================================
const TRIGGER_EVENTS = [
  { value: 'status_change',           label: 'Referral status change (community or TCO/service)' },
  { value: 'new_referral',            label: 'New referral created' },
  { value: 'provider_accepted',       label: 'Provider accepted referral (TCO/service)' },
  { value: 'sla_at_risk',             label: 'SLA at risk' },
  { value: 'renewal_due',             label: 'Medicaid renewal due window' },
  { value: 'consent_received',        label: 'Member consent received' },
  { value: 'workflow_step_completed', label: 'Workflow step completed (e.g. CCH care plan, ECM monthly contact, care gap closure)' },
  { value: 'workflow_completed',      label: 'Whole workflow completed' },
  // #4 — Claude classifies an inbound message and emits a verdict on the bus.
  { value: 'llm_classifier_verdict',  label: 'LLM classifier verdict (inbound-message intent)' },
];
const CLASSIFIER_LABELS = [
  'housing_instability', 'food_insecurity', 'transportation_barrier',
  'benefits_question', 'scheduling_intent', 'opt_out',
  'complaint', 'appointment_confirmed',
];
const SDOH_CATEGORIES = [
  '', 'housing', 'food', 'transportation', 'employment',
  'utilities', 'childcare', 'legal', 'mental_health',
  'substance_use', 'domestic_violence', 'language_access',
];
const REFERRAL_STATUSES = [
  '', 'matched', 'provider_notified', 'provider_accepted',
  'service_scheduled', 'service_completed',
  'pending_consent', 'cancelled', 'failed',
];
const WORKFLOW_CATEGORIES = [
  '', 'care_coordination', 'ecm_case_management', 'enrollment',
  'intake', 'medicaid_enrollment', 'outreach', 'workforce',
];
const STEP_TYPES = [
  '', 'approval', 'assessment', 'communication', 'decision',
  'document', 'external_data_ingest', 'form', 'task',
];

interface ConsentCategory {
  category_key: string;
  display_name: string;
  category_group?: string;
  requires_explicit_consent?: boolean;
}

interface RuleForm {
  name: string;
  description: string;
  trigger_event: string;
  sdoh_category: string;
  new_status: string;
  renewal_due_in_days_lte: string;
  workflow_template_code: string;
  workflow_category: string;
  step_code: string;
  step_type: string;
  campaign_template_id: string;
  channel_priority: string[];
  requires_consent_category: string;
  sla_hours: number;
  priority: number;
  // Phase 2 (migration 369)
  ai_channel_selection: boolean;
  send_window_local_start: string;   // '' or '0'..'23'
  send_window_local_end: string;
  max_touches_per_day: string;       // '' or positive int
  classifier_label: string;
  min_classifier_confidence: string; // stored under match_conditions.min_confidence
}

const blankForm = (): RuleForm => ({
  name: '', description: '',
  trigger_event: 'status_change',
  sdoh_category: '', new_status: '',
  renewal_due_in_days_lte: '',
  workflow_template_code: '', workflow_category: '',
  step_code: '', step_type: '',
  campaign_template_id: '',
  channel_priority: ['sms', 'email'],
  requires_consent_category: 'comm_sms',
  sla_hours: 72, priority: 100,
  ai_channel_selection: false,
  send_window_local_start: '',
  send_window_local_end: '',
  max_touches_per_day: '',
  classifier_label: '',
  min_classifier_confidence: '',
});

const buildMatchConditions = (f: RuleForm) => {
  const cond: Record<string, any> = {};
  if (f.sdoh_category) cond.sdoh_category = f.sdoh_category;
  if (f.new_status) cond.new_status = f.new_status;
  if (f.renewal_due_in_days_lte) cond.renewal_due_in_days_lte = parseInt(f.renewal_due_in_days_lte, 10);
  if (f.workflow_template_code) cond.workflow_template_code = f.workflow_template_code;
  if (f.workflow_category) cond.workflow_category = f.workflow_category;
  if (f.step_code) cond.step_code = f.step_code;
  if (f.step_type) cond.step_type = f.step_type;
  if (f.min_classifier_confidence) {
    const v = parseFloat(f.min_classifier_confidence);
    if (!isNaN(v)) cond.min_confidence = v;
  }
  return cond;
};

const RuleFormModal: React.FC<{
  cboId: number;
  initial?: RuleForm & { id?: number };
  templates: Array<{ id: number; name: string }>;
  consentCategories: ConsentCategory[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ cboId, initial, templates, consentCategories, onClose, onSaved }) => {
  const [f, setF] = useState<RuleForm>(initial || blankForm());
  const [busy, setBusy] = useState(false);
  const isEdit = !!(initial as any)?.id;
  const isWorkflowTrigger = f.trigger_event.startsWith('workflow');
  const isReferralTrigger = !isWorkflowTrigger && f.trigger_event !== 'renewal_due';
  const consentByGroup = (() => {
    const g: Record<string, ConsentCategory[]> = {};
    for (const c of consentCategories) {
      const k = c.category_group || 'other';
      (g[k] ||= []).push(c);
    }
    return g;
  })();

  const submit = async () => {
    setBusy(true);
    try {
      const payload: Record<string, any> = {
        cbo_id: cboId, name: f.name, description: f.description,
        trigger_event: f.trigger_event,
        match_conditions: buildMatchConditions(f),
        campaign_template_id: f.campaign_template_id ? parseInt(f.campaign_template_id, 10) : null,
        channel_priority: f.channel_priority,
        requires_consent_category: f.requires_consent_category || null,
        sla_hours: f.sla_hours, priority: f.priority,
        // Phase 2 (migration 369)
        ai_channel_selection: !!f.ai_channel_selection,
        send_window_local_start: f.send_window_local_start === '' ? null : parseInt(f.send_window_local_start, 10),
        send_window_local_end: f.send_window_local_end === '' ? null : parseInt(f.send_window_local_end, 10),
        max_touches_per_day: f.max_touches_per_day === '' ? null : parseInt(f.max_touches_per_day, 10),
        classifier_label: f.trigger_event === 'llm_classifier_verdict' ? (f.classifier_label || null) : null,
      };
      if (isEdit) {
        await authFetch(`/api/referral-automation/rules/${(initial as any).id}`, {
          method: 'PUT', jsonBody: payload,
        });
      } else {
        await authFetch('/api/referral-automation/rules', {
          method: 'POST', jsonBody: payload,
        });
      }
      onSaved();
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-lg bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{isEdit ? 'Edit rule' : 'New automation rule'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">✕</button>
        </div>
        <div className="space-y-3">
          <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })}
            placeholder="Rule name (e.g. SDOH Housing Accepted → Follow-up)"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm" />
          <textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })}
            placeholder="What this rule does"
            rows={2}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm" />

          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Trigger event</label>
            <select value={f.trigger_event} onChange={e => setF({ ...f, trigger_event: e.target.value })}
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm">
              {TRIGGER_EVENTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          {isReferralTrigger && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold uppercase text-gray-500">SDOH category</label>
                <select value={f.sdoh_category} onChange={e => setF({ ...f, sdoh_category: e.target.value })}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm">
                  {SDOH_CATEGORIES.map(c => <option key={c} value={c}>{c || '(any)'}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-gray-500">New status (TCO/service)</label>
                <select value={f.new_status} onChange={e => setF({ ...f, new_status: e.target.value })}
                  className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm">
                  {REFERRAL_STATUSES.map(s => <option key={s} value={s}>{s || '(any)'}</option>)}
                </select>
              </div>
            </div>
          )}

          {f.trigger_event === 'renewal_due' && (
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Renewal-due window (days)</label>
              <input type="number" value={f.renewal_due_in_days_lte}
                onChange={e => setF({ ...f, renewal_due_in_days_lte: e.target.value })}
                placeholder="60"
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
            </div>
          )}

          {f.trigger_event === 'llm_classifier_verdict' && (
            <div className="rounded border border-v2border bg-v2accent-soft p-3 text-xs text-v2accent-ink">
              <div className="mb-2 font-semibold">
                LLM classifier trigger — fires when an inbound message is
                classified as the chosen label
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold uppercase text-gray-500">Classifier label</label>
                  <select value={f.classifier_label}
                    onChange={e => setF({ ...f, classifier_label: e.target.value })}
                    className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm">
                    <option value="">(select a label)</option>
                    {CLASSIFIER_LABELS.map(l => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-gray-500">Min confidence (0–1)</label>
                  <input type="number" step="0.05" min="0" max="1"
                    value={f.min_classifier_confidence}
                    onChange={e => setF({ ...f, min_classifier_confidence: e.target.value })}
                    placeholder="e.g. 0.7"
                    className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm" />
                </div>
              </div>
              <p className="mt-2 text-purple-800">
                Inbound messages are classified by{' '}
                <span className="font-mono">POST /api/referral-automation/classify-message</span>{' '}
                — typically wired from chatflow or SMS webhook. The agent
                fires when label matches and confidence ≥ threshold.
              </p>
            </div>
          )}

          {isWorkflowTrigger && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <div className="mb-2 font-semibold">Workflow trigger — fires when a workflow_step_instance / workflow_instance hits &lsquo;completed&rsquo;</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold uppercase text-gray-500">Workflow template code</label>
                  <input value={f.workflow_template_code}
                    onChange={e => setF({ ...f, workflow_template_code: e.target.value })}
                    placeholder="e.g. cch_care_plan, care_transitions, ecm_monthly_contact"
                    className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-gray-500">Workflow category</label>
                  <select value={f.workflow_category}
                    onChange={e => setF({ ...f, workflow_category: e.target.value })}
                    className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm">
                    {WORKFLOW_CATEGORIES.map(c => <option key={c} value={c}>{c || '(any)'}</option>)}
                  </select>
                </div>
                {f.trigger_event === 'workflow_step_completed' && (
                  <>
                    <div>
                      <label className="text-xs font-bold uppercase text-gray-500">Step code</label>
                      <input value={f.step_code}
                        onChange={e => setF({ ...f, step_code: e.target.value })}
                        placeholder="e.g. close_diabetic_eye_gap"
                        className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase text-gray-500">Step type</label>
                      <select value={f.step_type}
                        onChange={e => setF({ ...f, step_type: e.target.value })}
                        className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm">
                        {STEP_TYPES.map(s => <option key={s} value={s}>{s || '(any)'}</option>)}
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="mt-2 text-amber-800">
                Examples: care_coordination &rarr; CCH Care Plan / Discharge Follow-Up step
                &lsquo;completed&rsquo; → fire member confirmation campaign. Care gap closure
                workflow &lsquo;close_diabetic_eye_gap&rsquo; step completed → fire incentive
                redemption reminder.
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Campaign / template to dispatch</label>
            <select value={f.campaign_template_id}
              onChange={e => setF({ ...f, campaign_template_id: e.target.value })}
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm">
              <option value="">(let agent pick from library)</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Channel priority (in order)</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {['sms', 'voice', 'email', 'whatsapp'].map(ch => (
                <label key={ch} className="flex items-center gap-1 text-sm">
                  <input type="checkbox"
                    checked={f.channel_priority.includes(ch)}
                    onChange={e => setF({
                      ...f,
                      channel_priority: e.target.checked
                        ? [...f.channel_priority, ch]
                        : f.channel_priority.filter(c => c !== ch),
                    })} />
                  {ch}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Required consent</label>
            <select value={f.requires_consent_category}
              onChange={e => setF({ ...f, requires_consent_category: e.target.value })}
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm">
              <option value="">(no consent gate)</option>
              {Object.entries(consentByGroup).map(([grp, cats]) => (
                <optgroup key={grp} label={grp}>
                  {cats.map(c => (
                    <option key={c.category_key} value={c.category_key}>
                      {c.display_name} ({c.category_key}){c.requires_explicit_consent ? ' · explicit' : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Resolves against <span className="font-mono">member_consent_categories</span>.
              For categories with <span className="font-mono">requires_explicit_consent=true</span>{' '}
              (SMS, email, voice, financial, clinical, referral data sharing, HIE),
              the agent blocks dispatch unless the member has a granted consent row.
            </p>
          </div>
          <div className="rounded border border-v2border bg-v2accent-soft p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold uppercase text-v2accent-ink">Dispatch options</div>
                <p className="text-[11px] text-v2accent-ink">
                  Cross-rule contention guard, AI channel selection, and
                  send-window controls (migration 369).
                </p>
              </div>
              <label className="flex items-center gap-2 text-xs text-blue-900">
                <input type="checkbox"
                  checked={f.ai_channel_selection}
                  onChange={e => setF({ ...f, ai_channel_selection: e.target.checked })} />
                <span><strong>AI-pick channel + hour</strong> per member</span>
              </label>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <div>
                <label className="text-[11px] font-bold uppercase text-gray-500">Send window start (local hr)</label>
                <input type="number" min="0" max="23"
                  value={f.send_window_local_start}
                  onChange={e => setF({ ...f, send_window_local_start: e.target.value })}
                  placeholder="9"
                  className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-gray-500">Send window end (local hr)</label>
                <input type="number" min="0" max="23"
                  value={f.send_window_local_end}
                  onChange={e => setF({ ...f, send_window_local_end: e.target.value })}
                  placeholder="20"
                  className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase text-gray-500">Max touches / day (any rule)</label>
                <input type="number" min="1"
                  value={f.max_touches_per_day}
                  onChange={e => setF({ ...f, max_touches_per_day: e.target.value })}
                  placeholder="3"
                  className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm" />
              </div>
            </div>
            <p className="mt-2 text-[11px] text-blue-900/70">
              If the member has already received this many auto-dispatches
              in the last 24h (across all rules for this org), this rule
              skips and logs a <span className="font-mono">contention_skip</span> decision.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">SLA hours</label>
              <input type="number" value={f.sla_hours}
                onChange={e => setF({ ...f, sla_hours: parseInt(e.target.value, 10) || 0 })}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Priority</label>
              <input type="number" value={f.priority}
                onChange={e => setF({ ...f, priority: parseInt(e.target.value, 10) || 0 })}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded bg-gray-100 px-3 py-1.5 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !f.name}
            className="rounded bg-v2accent px-3 py-1.5 text-sm text-white hover:bg-v2accent/90 disabled:opacity-50">
            {busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Create rule')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// AI suggested rules (above the rule list)
// ----------------------------------------------------------------------------
// Claude reads recent agent sessions + completions for the org and proposes
// new automation rules. Each suggestion can be accepted (creates the rule)
// or dismissed. Generation is rate-limited server-side (see migration 368
// and the cooldown logic in app/routers/uuhp_integration_flows.py).
// ============================================================================
interface RuleSuggestion {
  id: number;
  name: string;
  description?: string | null;
  trigger_event: string;
  match_conditions: Record<string, any>;
  channel_priority: string[];
  requires_consent_category?: string | null;
  sla_hours: number;
  priority: number;
  reasoning?: string | null;
  evidence?: Record<string, any>;
  confidence?: number | null;
  projected_lift?: string | null;
  model?: string | null;
  generated_at?: string | null;
}

const SuggestedRulesPanel: React.FC<{
  cboId: number;
  onAccepted: () => void;
}> = ({ cboId, onAccepted }) => {
  const [suggestions, setSuggestions] = useState<RuleSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldownSec, setCooldownSec] = useState<number>(0);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await authFetchJson<RuleSuggestion[]>(
        `/api/referral-automation/rules/suggestions?cbo_id=${cboId}&status=pending`,
      );
      setSuggestions(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [cboId]);

  const generate = async (force = false) => {
    setGenerating(true); setError(null); setCooldownSec(0);
    try {
      const res = await authFetch('/api/referral-automation/rules/suggestions/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cbo_id: cboId, max_suggestions: 5, force }),
      });
      if (res.status === 429) {
        const body = await res.json().catch(() => ({} as any));
        const detail = body?.detail || {};
        setCooldownSec(detail.retry_after_seconds || 0);
        setError(detail.message || 'Cooldown active');
        return;
      }
      if (!res.ok) {
        throw new Error(`Generation failed (${res.status})`);
      }
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Suggestion generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const accept = async (s: RuleSuggestion) => {
    try {
      const res = await authFetch(
        `/api/referral-automation/rules/suggestions/${s.id}/accept`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
      );
      if (!res.ok) throw new Error(`Accept failed (${res.status})`);
      await refresh();
      onAccepted();
    } catch (e: any) {
      setError(e?.message || 'Accept failed');
    }
  };

  const dismiss = async (s: RuleSuggestion) => {
    try {
      const res = await authFetch(
        `/api/referral-automation/rules/suggestions/${s.id}/dismiss`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
      );
      if (!res.ok) throw new Error(`Dismiss failed (${res.status})`);
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Dismiss failed');
    }
  };

  const hasSuggestions = suggestions.length > 0;

  return (
    <div className="rounded-lg border border-v2border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-v2accent px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
              AI
            </span>
            <h3 className="text-base font-semibold text-ink">Suggested rules</h3>
          </div>
          <p className="mt-1 text-xs text-ink-500">
            Claude reads your last 30 days of agent sessions, completions, and
            existing rules, then proposes high-leverage new automations.
            Accept to promote to a live rule; dismiss to drop it.
          </p>
        </div>
        <button
          onClick={() => generate(false)}
          disabled={generating}
          className="shrink-0 rounded bg-v2accent px-3 py-1.5 text-xs font-semibold text-white hover:bg-v2accent/90 disabled:opacity-60"
        >
          {generating ? 'Asking Claude…' : (hasSuggestions ? 'Refresh suggestions' : 'Generate suggestions')}
        </button>
      </div>

      {error && (
        <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {error}
          {cooldownSec > 0 && (
            <button
              onClick={() => generate(true)}
              className="ml-2 font-semibold text-amber-900 underline hover:text-amber-950"
            >
              Force regenerate
            </button>
          )}
        </div>
      )}

      {loading && !hasSuggestions && (
        <p className="mt-3 text-xs text-purple-900/70">Loading suggestions…</p>
      )}

      {!loading && !hasSuggestions && !error && (
        <p className="mt-3 text-xs text-purple-900/70">
          No pending suggestions. Click <strong>Generate suggestions</strong> to ask Claude.
        </p>
      )}

      {hasSuggestions && (
        <ul className="mt-3 space-y-2">
          {suggestions.map(s => (
            <li
              key={s.id}
              className="rounded-md border border-purple-200 bg-white p-3 text-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{s.name}</span>
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-600">
                      {s.trigger_event}
                    </span>
                    {typeof s.confidence === 'number' && (
                      <span className="text-[10px] uppercase tracking-wide text-gray-500">
                        {(s.confidence * 100).toFixed(0)}% conf
                      </span>
                    )}
                  </div>
                  {s.description && (
                    <p className="mt-0.5 text-xs text-gray-600">{s.description}</p>
                  )}
                  {s.reasoning && (
                    <p className="mt-1 text-xs text-purple-900/80">
                      <strong>Why:</strong> {s.reasoning}
                    </p>
                  )}
                  {s.projected_lift && (
                    <p className="mt-0.5 text-xs text-green-800">
                      <strong>Projected lift:</strong> {s.projected_lift}
                    </p>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
                    <span>Channels: {(s.channel_priority || []).join(', ')}</span>
                    {s.requires_consent_category && (
                      <span>· Consent: <span className="font-mono">{s.requires_consent_category}</span></span>
                    )}
                    <span>· SLA {s.sla_hours}h · priority {s.priority}</span>
                  </div>
                  {s.match_conditions && Object.keys(s.match_conditions).length > 0 && (
                    <details className="mt-1 text-[11px] text-gray-500">
                      <summary className="cursor-pointer">Match conditions</summary>
                      <pre className="mt-1 max-w-full overflow-x-auto rounded bg-gray-50 p-2 font-mono text-[10px]">
                        {JSON.stringify(s.match_conditions, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
                <div className="flex shrink-0 flex-col gap-1.5">
                  <button
                    onClick={() => accept(s)}
                    className="rounded bg-v2accent px-2.5 py-1 text-xs font-semibold text-white hover:bg-v2accent/90"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => dismiss(s)}
                    className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

interface RulePerformance {
  rule_id: number;
  sessions_30d: number;
  dispatched_30d: number;
  escalated_30d: number;
  completions_30d: number;
  conversion_pct_30d: number | null;
}

export const ReferralAutomationRuleEditor: React.FC<{ cboId: number }> = ({ cboId }) => {
  const tabbed = useTabbedChrome();
  const [rules, setRules] = useState<any[]>([]);
  const [performance, setPerformance] = useState<Record<number, RulePerformance>>({});
  const [templates, setTemplates] = useState<Array<{ id: number; name: string }>>([]);
  const [consentCategories, setConsentCategories] = useState<ConsentCategory[]>([]);
  const [editing, setEditing] = useState<(RuleForm & { id?: number }) | null>(null);
  const [showForm, setShowForm] = useState(false);

  const refresh = () => {
    authFetchJson<any[]>(`/api/referral-automation/rules?cbo_id=${cboId}`).then(setRules);
    authFetchJson<RulePerformance[]>(`/api/referral-automation/rules/performance?cbo_id=${cboId}`)
      .then(rows => {
        const map: Record<number, RulePerformance> = {};
        for (const r of rows || []) map[r.rule_id] = r;
        setPerformance(map);
      })
      .catch(() => { /* view may be empty in fresh orgs */ });
  };
  useEffect(() => {
    refresh();
    authFetchJson<Array<{ id: number; name: string }>>('/api/templates/library').then(t => setTemplates(t || []));
    authFetchJson<{ categories: ConsentCategory[] } | ConsentCategory[]>('/api/member-consent/categories')
      .then(d => {
        const list = Array.isArray(d) ? d : (d as any).categories || [];
        setConsentCategories(list);
      })
      .catch(() => setConsentCategories([]));
  }, [cboId]);

  const onDelete = async (id: number) => {
    if (!window.confirm('Delete this rule? Existing dispatched campaigns are unaffected.')) return;
    await authFetch(`/api/referral-automation/rules/${id}`, { method: 'DELETE' });
    refresh();
  };

  const ruleToForm = (r: any): RuleForm & { id: number } => ({
    id: r.id,
    name: r.name || '', description: r.description || '',
    trigger_event: r.trigger_event || 'status_change',
    sdoh_category: r.match_conditions?.sdoh_category || '',
    new_status: r.match_conditions?.new_status || '',
    renewal_due_in_days_lte: r.match_conditions?.renewal_due_in_days_lte != null
      ? String(r.match_conditions.renewal_due_in_days_lte) : '',
    workflow_template_code: r.match_conditions?.workflow_template_code || '',
    workflow_category: r.match_conditions?.workflow_category || '',
    step_code: r.match_conditions?.step_code || '',
    step_type: r.match_conditions?.step_type || '',
    campaign_template_id: r.campaign_template_id ? String(r.campaign_template_id) : '',
    channel_priority: r.channel_priority || ['sms', 'email'],
    requires_consent_category: r.requires_consent_category || '',
    sla_hours: r.sla_hours || 72, priority: r.priority || 100,
    ai_channel_selection: !!r.ai_channel_selection,
    send_window_local_start: r.send_window_local_start != null ? String(r.send_window_local_start) : '',
    send_window_local_end: r.send_window_local_end != null ? String(r.send_window_local_end) : '',
    max_touches_per_day: r.max_touches_per_day != null ? String(r.max_touches_per_day) : '',
    classifier_label: r.classifier_label || '',
    min_classifier_confidence: r.match_conditions?.min_confidence != null
      ? String(r.match_conditions.min_confidence) : '',
  });

  return (
    <div className="space-y-4">
      <div>
        {!tabbed && <h2 className="text-lg font-semibold text-gray-900">Campaign Automation</h2>}
        <p className={`text-sm text-gray-500 ${tabbed ? '' : 'mt-0.5'}`}>
          Author rules that fire outreach campaigns in response to referral and workflow events.
        </p>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <strong className="font-semibold">Not the same as referral orchestration.</strong>{' '}
        This page configures <em>engagement</em> (the messages, channels, and
        timing of follow-up campaigns).{' '}
        To configure how a referral is routed, matched to providers, or
        progressed through steps, use{' '}
        <a
          href="/care/workflows"
          className="font-semibold text-amber-900 underline hover:text-amber-950"
        >
          TCO Workflow Templates
        </a>{' '}
        and{' '}
        <a
          href="/care/service-referrals"
          className="font-semibold text-amber-900 underline hover:text-amber-950"
        >
          Service Referrals
        </a>
        . Campaign Automation listens to events emitted by those surfaces.
      </div>

      <div className="rounded-lg border border-v2border bg-v2accent-soft p-4 text-sm text-v2accent-ink">
        <div className="font-semibold">How automation rules integrate with referrals & TCO workflows</div>
        <p className="mt-1">
          The <span className="font-mono">ReferralAutomationAgent</span> subscribes
          to the in-process event bus. When an event arrives, the agent evaluates
          active rules in priority order; if all match conditions hold (SDOH
          category, new status, renewal window, workflow template / step code,
          step type) AND the member has granted the required consent
          (resolved against <span className="font-mono">member_consent_categories</span>),
          it asks Claude to pick the best library template and dispatches a
          single-member follow-up campaign. Every step + decision is persisted
          in <span className="font-mono">referral_agent_sessions</span> /{' '}
          <span className="font-mono">referral_agent_decisions</span> for audit.
        </p>
        <p className="mt-2 text-xs text-blue-700">
          <strong>Wired triggers:</strong> community referral <em>Accepted</em> ·
          service-referral <em>matched</em> / <em>provider_accepted</em> ·
          Medicaid <em>renewal_due</em> ·{' '}
          <em>workflow_step_completed</em> /{' '}
          <em>workflow_completed</em> across any
          workflow_template (Care Transitions, ECM Monthly Contact, CCH Care
          Plan Development, Discharge Follow-Up, care-gap closure, etc.).
        </p>
        <p className="mt-1 text-xs text-blue-700">
          <strong>Consent enforcement:</strong> required consent is one of the real
          rows in <span className="font-mono">member_consent_categories</span>{' '}
          (comm_sms, comm_email, comm_phone, data_sharing_referral, ...).
          For explicit categories, the agent fails closed if the member
          hasn&apos;t granted consent — no silent pass-through.
        </p>
      </div>

      <SuggestedRulesPanel cboId={cboId} onAccepted={refresh} />

      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Automation rules</h3>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          className="rounded bg-v2accent px-3 py-1.5 text-sm text-white hover:bg-v2accent/90">
          + New rule
        </button>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Trigger</th>
            <th className="px-3 py-2">Conditions</th>
            <th className="px-3 py-2">Channels</th>
            <th className="px-3 py-2">30-day performance</th>
            <th className="px-3 py-2">Active</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rules.map(r => {
            const perf = performance[r.id];
            const conv = perf?.conversion_pct_30d ?? null;
            const dispatched = perf?.dispatched_30d ?? 0;
            const underperforming = conv != null && conv < 10 && dispatched >= 5;
            const channelLabel = r.ai_channel_selection
              ? <span className="rounded bg-paper-2 px-1.5 py-0.5 text-[10px] font-bold uppercase text-ink-500">AI</span>
              : (r.channel_priority || []).join(', ');
            return (
              <tr key={r.id} className="border-b border-gray-100">
                <td className="px-3 py-2 font-medium">
                  {r.name}
                  {r.classifier_label && (
                    <span className="ml-2 rounded bg-paper-2 px-1.5 py-0.5 text-[10px] font-mono text-ink-500">
                      {r.classifier_label}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">{r.trigger_event}</td>
                <td className="px-3 py-2 font-mono text-xs">{JSON.stringify(r.match_conditions)}</td>
                <td className="px-3 py-2">{channelLabel}</td>
                <td className="px-3 py-2">
                  {perf ? (
                    <div className="flex flex-col leading-tight">
                      <span className={`text-xs font-semibold ${
                        conv == null ? 'text-gray-400'
                        : underperforming ? 'text-red-600'
                        : conv >= 25 ? 'text-green-700'
                        : 'text-gray-700'
                      }`}>
                        {conv == null ? '—' : `${conv.toFixed(1)}%`}
                        {underperforming && (
                          <span className="ml-1 rounded bg-red-50 px-1 py-0.5 text-[10px] font-medium text-red-700">
                            underperforming
                          </span>
                        )}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        {perf.dispatched_30d} dispatched · {perf.completions_30d} completed
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2">{r.is_active ? '✓' : '–'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => { setEditing(ruleToForm(r)); setShowForm(true); }}
                    className="text-blue-600 hover:underline">Edit</button>
                  <span className="mx-2 text-gray-300">|</span>
                  <button onClick={() => onDelete(r.id)}
                    className="text-red-600 hover:underline">Delete</button>
                </td>
              </tr>
            );
          })}
          {rules.length === 0 && (
            <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">
              No automation rules yet. Click <strong>+ New rule</strong> to create one.
            </td></tr>
          )}
        </tbody>
      </table>

      {showForm && (
        <RuleFormModal cboId={cboId} initial={editing || undefined}
          templates={templates}
          consentCategories={consentCategories}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={refresh} />
      )}
    </div>
  );
};

// ============================================================================
// Flow 6 — AgentSessionDetail
// ============================================================================
export const AgentSessionDetail: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    authFetchJson<any>(`/api/referral-automation/sessions/${sessionId}`).then(setData);
  }, [sessionId]);
  if (!data) return <p className="text-sm text-gray-500">Loading session…</p>;
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase text-gray-500">Session</div>
        <div className="font-mono text-xs">{sessionId}</div>
        <div className="mt-1 inline-block rounded-full bg-paper-2 px-2 py-0.5 text-xs font-bold uppercase text-ink-500">
          {data.session.state}
        </div>
      </div>
      <div>
        <h4 className="mb-2 text-sm font-bold uppercase text-gray-500">Decisions</h4>
        <ol className="space-y-2">
          {(data.decisions || []).map((d: any, i: number) => (
            <li key={i} className="rounded border border-gray-200 bg-white p-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-paper-2 px-2 py-0.5 text-[10px] uppercase text-ink-500">
                  {d.decision_type}
                </span>
                <span className="text-xs text-gray-500">{d.model || '—'}</span>
                <span className="ml-auto text-xs text-gray-500">
                  {d.latency_ms != null ? `${d.latency_ms} ms` : ''}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-700"><strong>Prompt:</strong> {d.prompt_summary}</div>
              <div className="mt-1 text-xs text-gray-700"><strong>Response:</strong> {d.llm_response_summary}</div>
              <div className="mt-1 text-xs text-gray-500">
                Decision: {d.decision} · Confidence {(d.confidence_score * 100 || 0).toFixed(0)}%
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

// ============================================================================
// Flow 1 — JourneyStepLanguageGrid (side-by-side multi-language editor)
// ============================================================================
const LANG_LABEL: Record<string, string> = {
  en: 'English', es: 'Español', vi: 'Tiếng Việt',
  zh: '中文', ar: 'العربية', ko: '한국어',
};

interface VariantRow {
  language: string;
  content: string;
  subject: string | null;
  auto_translated: boolean;
  translation_provider?: string | null;
}

const isEmailStep = (step: any): boolean => {
  if (!step) return false;
  if (step.engagement_type === 'email') return true;
  // Email steps in this seed are 'message' type with email_subject set
  return Boolean(step.email_subject) || /email/i.test(step.name || '');
};

export const JourneyStepLanguageGrid: React.FC<{
  campaignId: string;
  step: any;
  enabledLanguages: string[];
  onSaved?: () => void;
}> = ({ campaignId, step, enabledLanguages, onSaved }) => {
  const [variants, setVariants] = useState<Record<string, VariantRow>>({});
  const [drafts, setDrafts] = useState<Record<string, { content: string; subject: string }>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  const isEmail = isEmailStep(step);

  // The variants table FKs to campaign_engagements.id (join row UUID), NOT
  // engagement_id. Engagement-rows now returns both; prefer the join row id
  // and fall back to engagement_id for older callers.
  const stepRowId: string | undefined = step?.id || step?.campaign_engagement_id || step?.engagement_id;

  const refresh = async () => {
    if (!stepRowId) return;
    setLoading(true);
    try {
      const list = await authFetchJson<VariantRow[]>(
        `/api/campaigns/${campaignId}/steps/${stepRowId}/language-variants`,
      );
      const map: Record<string, VariantRow> = {};
      for (const v of list) map[v.language] = v;
      // Seed English from the source content_template for parity
      if (!map['en']) {
        map['en'] = {
          language: 'en',
          content: step.content_template || '',
          subject: step.email_subject || null,
          auto_translated: false,
          translation_provider: 'source',
        };
      }
      setVariants(map);
      const d: Record<string, { content: string; subject: string }> = {};
      for (const lang of enabledLanguages) {
        d[lang] = {
          content: map[lang]?.content || '',
          subject: map[lang]?.subject || '',
        };
      }
      setDrafts(d);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [campaignId, stepRowId, enabledLanguages.join(',')]);

  const saveOne = async (lang: string) => {
    if (lang === 'en') return; // English sources from the engagement row, no per-lang override
    if (!stepRowId) return;
    setSaving(lang); setSavedFlash(null);
    try {
      await authFetch(`/api/campaigns/${campaignId}/steps/${stepRowId}/language-variants/${lang}`, {
        method: 'PUT',
        jsonBody: {
          content: drafts[lang]?.content || '',
          subject: isEmail ? (drafts[lang]?.subject || null) : null,
        },
      });
      setSavedFlash(lang);
      onSaved?.();
      // Optimistically update variant flags
      setVariants(prev => ({
        ...prev,
        [lang]: {
          ...(prev[lang] || { language: lang, content: '', subject: null, auto_translated: true }),
          content: drafts[lang]?.content || '',
          subject: drafts[lang]?.subject || null,
          auto_translated: false,
          translation_provider: 'manual',
        },
      }));
    } finally {
      setSaving(null);
      setTimeout(() => setSavedFlash(prev => (prev === lang ? null : prev)), 1500);
    }
  };

  if (!step) {
    return <p className="text-sm text-gray-500">Pick a step above to edit its language variants.</p>;
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
        <span className="rounded-full bg-paper-2 px-2 py-0.5 font-bold uppercase text-ink-500">
          Step {step.engagement_order ?? '?'}
        </span>
        <span className="font-medium text-gray-900">{step.name}</span>
        <span className="rounded bg-gray-100 px-2 py-0.5 uppercase text-gray-600">
          {step.engagement_type}{isEmail ? ' · email' : ''}
        </span>
        {loading && <span className="ml-auto text-gray-500">Loading variants…</span>}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        {enabledLanguages.map(lang => {
          const v = variants[lang];
          const d = drafts[lang] || { content: '', subject: '' };
          const dirty = (v?.content || '') !== d.content || (v?.subject || '') !== d.subject;
          const isStub = v?.translation_provider === 'stub';
          const isAuto = v?.auto_translated;
          return (
            <div key={lang} className={`rounded-lg border p-3 ${
              lang === 'en' ? 'border-gray-300 bg-gray-50' :
              isStub ? 'border-amber-300 bg-amber-50' :
              'border-gray-200 bg-white'
            }`}>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-lg">{LANG_FLAG[lang] || '🌐'}</span>
                <div>
                  <div className="text-xs font-bold uppercase text-gray-700">{lang}</div>
                  <div className="text-[10px] text-gray-500">{LANG_LABEL[lang] || lang}</div>
                </div>
                <span className="ml-auto text-[10px]">
                  {lang === 'en' && (
                    <span className="rounded bg-gray-200 px-1.5 py-0.5 font-bold uppercase text-gray-700">source</span>
                  )}
                  {lang !== 'en' && isStub && (
                    <span className="rounded bg-amber-200 px-1.5 py-0.5 font-bold uppercase text-amber-900">stub · review</span>
                  )}
                  {lang !== 'en' && !isStub && isAuto && (
                    <span className="rounded bg-paper-2 px-1.5 py-0.5 font-bold uppercase text-ink-500">auto</span>
                  )}
                  {lang !== 'en' && !isAuto && !isStub && v && (
                    <span className="rounded bg-paper-2 px-1.5 py-0.5 font-bold uppercase text-ink-500">manual</span>
                  )}
                </span>
              </div>
              {isEmail && (
                <input
                  value={d.subject}
                  onChange={e => setDrafts(prev => ({ ...prev, [lang]: { ...prev[lang], subject: e.target.value } }))}
                  placeholder="Email subject"
                  disabled={lang === 'en'}
                  className="mb-2 w-full rounded border border-gray-200 px-2 py-1 text-xs disabled:bg-gray-100"
                />
              )}
              <textarea
                value={d.content}
                onChange={e => setDrafts(prev => ({ ...prev, [lang]: { ...prev[lang], content: e.target.value } }))}
                rows={isEmail ? 5 : 4}
                disabled={lang === 'en'}
                className="w-full rounded border border-gray-200 px-2 py-1 text-xs disabled:bg-gray-100"
                placeholder={`Content for ${lang.toUpperCase()}…`}
              />
              <div className="mt-2 flex items-center justify-between">
                {lang === 'en' ? (
                  <span className="text-[10px] text-gray-500">Source — edit on the engagement row</span>
                ) : (
                  <button
                    onClick={() => saveOne(lang)}
                    disabled={!dirty || saving === lang}
                    className="rounded bg-v2accent px-2 py-1 text-[11px] font-medium text-white hover:bg-v2accent/90 disabled:opacity-40">
                    {saving === lang ? 'Saving…' : dirty ? 'Save override' : 'Saved'}
                  </button>
                )}
                {savedFlash === lang && (
                  <span className="text-[10px] text-green-700">✓ Saved</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ============================================================================
// Flow 1 — JourneyStepDragDrop (drag-and-drop journey ordering)
// ============================================================================
interface EngagementRow {
  engagement_id: string;
  engagement_order: number;
  delay_hours: number;
  condition_type: string;
  condition_config: any;
  is_ab_test: boolean;
  ab_variant: string | null;
  ab_percentage: number;
  is_parallel: boolean;
  timeout_hours: number;
  timeout_action: string;
  channel_override: string | null;
  name: string;
  engagement_type: string;
  content_template: string;
}

const STEP_ICON: Record<string, string> = {
  sms: '💬', email: '📧', voice: '☎️', whatsapp: '💚', ivr: '☎️', mail: '✉️',
};

const SortableStep: React.FC<{ row: EngagementRow; index: number }> = ({ row, index }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.engagement_id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}
      className="flex items-stretch gap-3 rounded-lg border border-gray-200 bg-white shadow-sm">
      <button {...attributes} {...listeners}
        className="flex w-10 cursor-grab items-center justify-center rounded-l-lg bg-gray-50 text-gray-400 hover:text-gray-700 active:cursor-grabbing"
        aria-label="Drag to reorder">
        ⋮⋮
      </button>
      <div className="flex-1 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-paper-2 px-2 py-0.5 text-xs font-bold uppercase text-ink-500">
            Step {index + 1}
          </span>
          <span className="text-base">{STEP_ICON[row.engagement_type] || '📨'}</span>
          <span className="text-sm font-medium text-gray-900">{row.name}</span>
          <span className="ml-auto rounded bg-gray-100 px-2 py-0.5 text-[10px] uppercase text-gray-600">
            {row.engagement_type}
          </span>
        </div>
        <div className="mt-1 line-clamp-2 text-xs text-gray-500">
          {row.delay_hours > 0 && (
            <span className="mr-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">
              wait {row.delay_hours}h
            </span>
          )}
          {row.condition_type !== 'always' && (
            <span className="mr-2 rounded bg-paper-2 px-1.5 py-0.5 text-[10px] font-bold uppercase text-ink-500">
              if {row.condition_type}
            </span>
          )}
          {row.content_template?.slice(0, 110)}
        </div>
      </div>
    </div>
  );
};

export const JourneyStepDragDrop: React.FC<{
  campaignId: string;
  cboId: number;
  onSaved?: () => void;
}> = ({ campaignId, cboId, onSaved }) => {
  const [rows, setRows] = useState<EngagementRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await authFetchJson<EngagementRow[]>(
        `/api/campaigns/${campaignId}/engagement-rows?cbo_id=${cboId}`,
      );
      setRows(Array.isArray(data) ? data : []);
      setDirty(false);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [campaignId, cboId]);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows(items => {
      const oldIndex = items.findIndex(r => r.engagement_id === active.id);
      const newIndex = items.findIndex(r => r.engagement_id === over.id);
      const moved = arrayMove(items, oldIndex, newIndex);
      return moved.map((r, i) => ({ ...r, engagement_order: i + 1 }));
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await authFetch(`/api/campaigns/${campaignId}/engagement-order`, {
        method: 'PUT',
        jsonBody: {
          cbo_id: cboId,
          order: rows.map(r => ({
            engagement_id: r.engagement_id,
            engagement_order: r.engagement_order,
          })),
        },
      });
      setDirty(false);
      setSavedAt(new Date());
      onSaved?.();
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-v2border bg-v2accent-soft p-3 text-xs text-v2accent-ink">
        <strong>Drag to reorder.</strong> Grab the ⋮⋮ handle on the left of any
        step and drop it where you want it. <span className="font-mono">engagement_order</span>{' '}
        is rewritten on save and the journey runtime picks up the new sequence
        on the next dispatch tick.
      </div>
      {loading && <p className="text-sm text-gray-500">Loading journey…</p>}
      {!loading && rows.length === 0 && (
        <p className="text-sm text-gray-500">No steps in this journey yet.</p>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={rows.map(r => r.engagement_id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {rows.map((r, i) => <SortableStep key={r.engagement_id} row={r} index={i} />)}
          </div>
        </SortableContext>
      </DndContext>
      <div className="flex items-center gap-3">
        <button disabled={!dirty || saving} onClick={save}
          className="rounded bg-v2accent px-3 py-1.5 text-sm text-white hover:bg-v2accent/90 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save order'}
        </button>
        <button disabled={!dirty || saving} onClick={refresh}
          className="rounded bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200 disabled:opacity-50">
          Reset
        </button>
        {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
        {!dirty && savedAt && (
          <span className="text-xs text-green-700">Saved {savedAt.toLocaleTimeString()}</span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Flow 2 — SimulateInboundSMSButton (stage an inbound SMS through real classifier)
// ============================================================================
const SAMPLE_INBOUND: Array<{ label: string; lang: string; body: string }> = [
  { label: 'Spanish — renewal help (Sofia)', lang: 'es',
    body: 'Hola, recibí una carta sobre mi Medicaid pero no entiendo qué tengo que hacer. ¿Me pueden ayudar a renovar?' },
  { label: 'Spanish — address change', lang: 'es',
    body: 'Necesito cambiar mi dirección, me mudé la semana pasada.' },
  { label: 'Vietnamese — benefits question', lang: 'vi',
    body: 'Tôi muốn biết bảo hiểm của tôi có chi trả cho khám mắt không?' },
  { label: 'English — STOP keyword (opt-out)', lang: 'en', body: 'STOP' },
  { label: 'English — complaint', lang: 'en',
    body: "I've been waiting 3 weeks for someone to call me back about my appointment. This is unacceptable." },
];

interface MemberOption {
  id: number;
  first_name: string | null;
  last_name: string | null;
  preferred_language?: string | null;
  language?: string | null;
}

export const SimulateInboundSMSButton: React.FC<{
  organizationId: number;
  defaultMemberId?: number;
  onCreated?: (handoffId: string | null) => void;
}> = ({ organizationId, defaultMemberId, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [memberId, setMemberId] = useState<string>(defaultMemberId ? String(defaultMemberId) : '');
  const [memberOptions, setMemberOptions] = useState<MemberOption[]>([]);
  const [body, setBody] = useState<string>(SAMPLE_INBOUND[0].body);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Load member options when modal opens
  useEffect(() => {
    if (!open) return;
    if (memberOptions.length > 0) return;
    authFetchJson<{ members: MemberOption[] } | MemberOption[]>(
      `/api/members?page=1&page_size=500`,
    )
      .then(d => {
        const list = Array.isArray(d) ? d : ((d as any).members || []);
        setMemberOptions(list);
        // Default to a Spanish-speaking member if available so the demo lands well
        if (!memberId && list.length > 0) {
          const sofia = list.find(m =>
            (m.preferred_language === 'es' || m.language === 'Spanish') &&
            (m.first_name || '').toLowerCase().includes('sofia')
          );
          setMemberId(String(sofia?.id || list[0].id));
        }
      })
      .catch(() => setMemberOptions([]));
  }, [open]);

  const submit = async () => {
    if (!memberId) return;
    setBusy(true); setResult(null); setError(null);
    try {
      const res = await authFetch('/api/handoffs/simulate-inbound', {
        method: 'POST',
        jsonBody: {
          organization_id: organizationId,
          member_id: parseInt(memberId, 10),
          body,
        },
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${txt || res.statusText}`);
      }
      const j = await res.json();
      setResult(j);
      onCreated?.(j.handoff_id || null);
    } catch (e: any) {
      console.error('simulate inbound failed', e);
      setError(String(e?.message || e) || 'Failed to simulate inbound SMS');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="rounded bg-v2accent px-2 py-1 text-xs text-white hover:bg-v2accent/90">
        ⚡ Simulate inbound SMS
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold">Simulate inbound SMS</h2>
              <button onClick={() => { setOpen(false); setResult(null); }}
                className="text-gray-500 hover:text-gray-900">✕</button>
            </div>
            <p className="mb-3 text-xs text-gray-500">
              Runs the full intent-classification pipeline (auto-translate &rarr;
              Claude classify &rarr; auto-handoff if{' '}
              <span className="font-mono">needs_human=true</span>) — same code path
              the Twilio webhook hits. The new handoff (if any) will appear in the
              CSR inbox within 10 seconds.
            </p>
            <label className="mb-1 block text-xs font-bold uppercase text-gray-500">Member</label>
            <select value={memberId} onChange={e => setMemberId(e.target.value)}
              className="mb-2 w-full rounded border border-gray-300 px-3 py-2 text-sm">
              {memberOptions.length === 0 && <option value="">(loading members…)</option>}
              {memberOptions.map(m => {
                const lang = m.preferred_language || m.language || '';
                const flag = lang === 'es' || /spanish/i.test(lang) ? '🇲🇽'
                  : lang === 'vi' || /vietnamese/i.test(lang) ? '🇻🇳'
                  : lang === 'zh' || /chinese/i.test(lang) ? '🇨🇳'
                  : lang === 'ar' || /arabic/i.test(lang) ? '🇸🇦'
                  : lang === 'ko' || /korean/i.test(lang) ? '🇰🇷'
                  : '🇺🇸';
                return (
                  <option key={m.id} value={m.id}>
                    {flag} {m.first_name || ''} {m.last_name || ''} · #{m.id}
                  </option>
                );
              })}
            </select>
            <div className="mb-2 flex flex-wrap gap-1">
              {SAMPLE_INBOUND.map(s => (
                <button key={s.label} onClick={() => setBody(s.body)}
                  className="rounded-full bg-gray-100 px-2 py-1 text-[11px] hover:bg-gray-200">
                  {s.label}
                </button>
              ))}
            </div>
            <textarea value={body} onChange={e => setBody(e.target.value)}
              rows={4}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              placeholder="Free text the member 'sent'…" />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => { setOpen(false); setResult(null); }}
                className="rounded bg-gray-100 px-3 py-1.5 text-sm">Close</button>
              <button onClick={submit} disabled={!memberId || busy}
                className="rounded bg-v2accent px-3 py-1.5 text-sm text-white hover:bg-v2accent/90 disabled:opacity-50">
                {busy ? 'Classifying…' : 'Send'}
              </button>
            </div>
            {error && (
              <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                {error}
              </div>
            )}
            {result && (
              <div className="mt-4 rounded-lg bg-gray-50 p-3 text-xs text-gray-700">
                <div><strong>Detected:</strong> {result.detected_language} · intent <span className="font-mono">{result.intent_label}</span> ({((result.intent_confidence || 0) * 100).toFixed(0)}%)</div>
                {result.english_text && (
                  <div className="mt-1"><strong>English:</strong> {result.english_text}</div>
                )}
                <div className="mt-1">
                  {result.needs_human ? (
                    <span className="rounded bg-green-100 px-2 py-0.5 font-bold uppercase text-green-700">
                      ✓ Handoff created — id {String(result.handoff_id).slice(0, 8)}…
                    </span>
                  ) : (
                    <span className="rounded bg-gray-200 px-2 py-0.5 font-bold uppercase text-gray-700">
                      No handoff (needs_human=false)
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// Flow 7 — IncentiveCompletionTrigger (close the loop on stage)
// ============================================================================
interface IncentiveConfig {
  id: number;
  config_number: string;
  incentive_name: string;
  incentive_type: string;
  monetary_amount: number | null;
  currency?: string | null;
  badge_name?: string | null;
  achievement_level?: string | null;
}

export const IncentiveCompletionTrigger: React.FC<{
  memberId: number;
  cboId: number;
  onCompleted?: (earningId: number) => void;
}> = ({ memberId, cboId, onCompleted }) => {
  const [open, setOpen] = useState(false);
  const [configs, setConfigs] = useState<IncentiveConfig[]>([]);
  const [pickedId, setPickedId] = useState<string>('');
  const [autoPay, setAutoPay] = useState<boolean>(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    if (!open) return;
    authFetchJson<IncentiveConfig[]>(`/api/incentives/configurations?limit=100`)
      .then(list => {
        setConfigs(list || []);
        if (list?.length && !pickedId) setPickedId(String(list[0].id));
      })
      .catch(() => setConfigs([]));
  }, [open]);

  const submit = async () => {
    if (!pickedId) return;
    setBusy(true); setResult(null);
    try {
      const res = await authFetch('/api/incentives/demo/trigger-completion', {
        method: 'POST',
        jsonBody: {
          member_id: memberId,
          configuration_id: parseInt(pickedId, 10),
          auto_pay: autoPay,
          note: 'Triggered from member achievements panel',
        },
      });
      const j = await res.json();
      setResult(j);
      onCompleted?.(j.earning_id);
    } finally { setBusy(false); }
  };

  const picked = configs.find(c => String(c.id) === pickedId);

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="rounded bg-v2accent px-3 py-1.5 text-sm text-white hover:bg-v2accent/90">
        🎯 Mark action complete
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold">Trigger incentive completion</h2>
              <button onClick={() => { setOpen(false); setResult(null); }}
                className="text-gray-500 hover:text-gray-900">✕</button>
            </div>
            <p className="mb-3 text-xs text-gray-500">
              Closes the HEDIS gap end-to-end: writes a fresh row in{' '}
              <span className="font-mono">incentive_earnings</span>, and if{' '}
              <em>auto-pay</em> is on and the config is monetary, immediately
              issues a mock gift card and flips the row to PAID. Sofia's
              dashboard refreshes when you close this dialog.
            </p>
            <label className="mb-1 block text-xs font-bold uppercase text-gray-500">
              Incentive configuration
            </label>
            <select value={pickedId} onChange={e => setPickedId(e.target.value)}
              className="mb-3 w-full rounded border border-gray-300 px-3 py-2 text-sm">
              {configs.map(c => (
                <option key={c.id} value={c.id}>
                  {c.incentive_name}
                  {c.incentive_type === 'MONETARY' && c.monetary_amount
                    ? ` — $${Number(c.monetary_amount).toFixed(2)}`
                    : ''}
                  {c.incentive_type === 'BADGE' && c.badge_name
                    ? ` — badge: ${c.badge_name}`
                    : ''}
                  {c.incentive_type === 'ACHIEVEMENT_LEVEL' && c.achievement_level
                    ? ` — level: ${c.achievement_level}`
                    : ''}
                </option>
              ))}
            </select>
            {picked?.incentive_type === 'MONETARY' && (
              <label className="mb-3 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={autoPay} onChange={e => setAutoPay(e.target.checked)} />
                Auto-pay (issue mock gift card and mark PAID)
              </label>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => { setOpen(false); setResult(null); }}
                className="rounded bg-gray-100 px-3 py-1.5 text-sm">Close</button>
              <button onClick={submit} disabled={!pickedId || busy}
                className="rounded bg-v2accent px-3 py-1.5 text-sm text-white hover:bg-v2accent/90 disabled:opacity-50">
                {busy ? 'Processing…' : 'Trigger completion'}
              </button>
            </div>
            {result && (
              <div className="mt-4 rounded-lg bg-emerald-50 p-3 text-xs text-emerald-900">
                <div><strong>{result.configuration_name}</strong> · {result.incentive_type}</div>
                <div className="mt-1">Earning <span className="font-mono">{result.earning_number}</span> · status <strong>{result.final_status}</strong></div>
                {result.giftcard?.redemption_url && (
                  <div className="mt-2">
                    Gift card sent · <span className="font-mono">{result.giftcard.redemption_code_masked}</span>{' '}
                    · <a href={result.giftcard.redemption_url} target="_blank" rel="noopener noreferrer"
                         className="font-semibold underline">Redeem link</a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ============================================================================
// Flow 3 — UUHPCampaignProgressPage (full reporting page)
// ============================================================================
interface CampaignSummary {
  id: string;
  name: string;
  status: string;
  approval_status?: string | null;
}

interface ProductLine {
  lob_id: number;
  lob_type: string;
  lob_name: string;
  description: string | null;
  state: string | null;
  region: string | null;
  member_count: number;
  campaign_count: number;
  products: Array<{
    id: number;
    product_code: string;
    name: string;
    product_type: string;
    metal_tier: string | null;
    member_count: number;
    campaign_count: number;
  }>;
}

interface CampaignWithProduct extends CampaignSummary {
  lob_id?: number | null;
  product_id?: number | null;
}

export const UUHPCampaignProgressPage: React.FC<{
  cboId: number;
}> = ({ cboId }) => {
  const [campaigns, setCampaigns] = useState<CampaignWithProduct[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overview, setOverview] = useState<any>(null);
  const [windowSel, setWindowSel] = useState<'7d' | '14d' | '30d'>('7d');
  const [showSchedule, setShowSchedule] = useState(false);
  const [loading, setLoading] = useState(false);
  const [memberDrill, setMemberDrill] = useState<number | null>(null);
  const [productLines, setProductLines] = useState<ProductLine[]>([]);
  const [filterLobId, setFilterLobId] = useState<number | null>(null);
  const [filterProductId, setFilterProductId] = useState<number | null>(null);

  // Load product lines (LOBs + products) for filtering
  useEffect(() => {
    authFetchJson<ProductLine[]>(`/api/orgs/${cboId}/product-lines`)
      .then(d => setProductLines(Array.isArray(d) ? d : []))
      .catch(() => setProductLines([]));
  }, [cboId]);

  // Load campaigns
  useEffect(() => {
    setLoading(true);
    authFetchJson<any>(`/api/campaigns?cbo_id=${cboId}&limit=50`)
      .then(d => {
        const items: CampaignWithProduct[] = Array.isArray(d) ? d : (d.items || d.campaigns || []);
        setCampaigns(items);
        if (items.length && !activeId) setActiveId(items[0].id);
      })
      .catch(() => setCampaigns([]))
      .finally(() => setLoading(false));
  }, [cboId]);

  // Load overview when campaign or window changes
  useEffect(() => {
    if (!activeId) return;
    authFetchJson<any>(
      `/api/campaigns/${activeId}/analytics?cbo_id=${cboId}&window=${windowSel}`,
    ).then(setOverview).catch(() => setOverview(null));
  }, [activeId, cboId, windowSel]);

  const overviewStats = overview?.overview || overview || {};
  const sentCount = overviewStats.sent ?? overviewStats.sent_count ?? 0;
  const deliveryRate = overviewStats.delivery_rate ?? 0;
  const responseRate = overviewStats.response_rate ?? 0;
  const optOutRate = overviewStats.opt_out_rate ?? 0;
  const bounceRate = overviewStats.bounce_rate ?? 0;

  const Tile: React.FC<{ label: string; value: string; tone?: 'good' | 'warn' | 'bad' }> = ({ label, value, tone }) => (
    <div className={`rounded-lg border p-4 ${
      tone === 'good' ? 'border-green-200 bg-green-50' :
      tone === 'warn' ? 'border-amber-200 bg-amber-50' :
      tone === 'bad' ? 'border-red-200 bg-red-50' :
      'border-gray-200 bg-white'
    }`}>
      <div className="text-xs font-bold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-v2border bg-v2accent-soft p-4 text-sm text-v2accent-ink">
        <div className="font-semibold">Live campaign progress · TCPA-compliant reporting</div>
        <p className="mt-1">
          Every metric on this page is computed from the{' '}
          <span className="font-mono">engagement_events</span> table — the same
          event log the runtime writes when SMS, email, voice, and IVR steps
          fire. Drill into <strong>language</strong> or <strong>channel</strong>{' '}
          to find under-performing cohorts. Schedule a weekly auto-report to
          email the recap to your team — no human in the loop.
        </p>
      </div>

      {productLines.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">
            Product line
            <span className="ml-2 text-[10px] font-normal normal-case text-gray-400">
              Group campaigns under their line of business and product
            </span>
          </h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setFilterLobId(null); setFilterProductId(null); }}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                filterLobId === null
                  ? 'bg-v2accent text-white'
                  : 'border border-v2border bg-surface text-ink-500 hover:text-ink'
              }`}>
              All ({productLines.reduce((s, l) => s + l.campaign_count, 0)})
            </button>
            {productLines.map(l => (
              <button key={l.lob_id}
                onClick={() => { setFilterLobId(l.lob_id); setFilterProductId(null); }}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  filterLobId === l.lob_id && filterProductId === null
                    ? 'bg-v2accent text-white'
                    : 'border border-v2border bg-surface text-ink-500 hover:text-ink'
                }`}>
                {l.lob_name}{' '}
                <span className="opacity-70">({l.member_count}m · {l.campaign_count}c)</span>
              </button>
            ))}
          </div>
          {filterLobId !== null && (() => {
            const sel = productLines.find(l => l.lob_id === filterLobId);
            if (!sel || sel.products.length === 0) return null;
            return (
              <div className="mt-2 flex flex-wrap gap-2 border-t border-gray-100 pt-2">
                {sel.products.map(p => (
                  <button key={p.id}
                    onClick={() => setFilterProductId(filterProductId === p.id ? null : p.id)}
                    className={`rounded-full px-2.5 py-0.5 text-[11px] ${
                      filterProductId === p.id
                        ? 'bg-blue-200 text-blue-900'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}>
                    {p.name}
                    {p.metal_tier ? <span className="ml-1 opacity-60">· {p.metal_tier}</span> : null}
                    <span className="ml-1 opacity-50">({p.member_count}m · {p.campaign_count}c)</span>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-bold uppercase text-gray-500">Campaign</label>
        <select value={activeId || ''} onChange={e => setActiveId(e.target.value || null)}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm">
          {loading && <option>Loading…</option>}
          {!loading && (() => {
            const visible = campaigns.filter(c =>
              (filterLobId === null || c.lob_id === filterLobId) &&
              (filterProductId === null || c.product_id === filterProductId),
            );
            if (visible.length === 0) return <option>(no campaigns in selected product line)</option>;
            return visible.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ));
          })()}
        </select>
        <select value={windowSel} onChange={e => setWindowSel(e.target.value as any)}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm">
          <option value="7d">Last 7 days</option>
          <option value="14d">Last 14 days</option>
          <option value="30d">Last 30 days</option>
        </select>
        {activeId && (
          <>
            <ExportMenuButton cboId={cboId} campaignId={activeId} window={windowSel} />
            <button onClick={() => setShowSchedule(true)}
              className="rounded bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200">
              Schedule weekly report
            </button>
          </>
        )}
      </div>

      {activeId && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Tile label="Sent" value={Number(sentCount).toLocaleString()} />
            <Tile label="Delivery rate" value={`${(deliveryRate * 100).toFixed(1)}%`}
              tone={deliveryRate > 0.85 ? 'good' : deliveryRate > 0.7 ? 'warn' : 'bad'} />
            <Tile label="Response rate" value={`${(responseRate * 100).toFixed(1)}%`}
              tone={responseRate > 0.1 ? 'good' : 'warn'} />
            <Tile label="Opt-out rate" value={`${(optOutRate * 100).toFixed(1)}%`}
              tone={optOutRate < 0.02 ? 'good' : 'warn'} />
            <Tile label="Bounce rate" value={`${(bounceRate * 100).toFixed(1)}%`}
              tone={bounceRate < 0.05 ? 'good' : 'bad'} />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">Engagements (per step)</h3>
              <span className="text-xs text-gray-500">
                A campaign is a series of engagements. Each row below is one outbound step.
              </span>
            </div>
            <CampaignEngagementBreakdown cboId={cboId} campaignId={activeId} window={windowSel} />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">By language</h3>
            <CampaignDimensionBreakdown cboId={cboId} campaignId={activeId} dim="language" window={windowSel} />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">By channel</h3>
            <CampaignDimensionBreakdown cboId={cboId} campaignId={activeId} dim="channel" window={windowSel} />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">Member-level drill-down</h3>
              <span className="text-xs text-gray-500">Click any member to see their full engagement timeline</span>
            </div>
            <CampaignTopMembers cboId={cboId} campaignId={activeId} window={windowSel}
              onPickMember={mid => setMemberDrill(mid)} />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm">
            Need to reorder steps, edit language variants, or request approval?
            Open the dedicated{' '}
            <button
              onClick={() => {
                const w = window as any;
                if (typeof w.__navigateWithHistory === 'function') w.__navigateWithHistory('journey-builder');
              }}
              className="font-semibold text-blue-700 underline hover:text-blue-900">
              Campaign Builder →
            </button>
            {' '}for this campaign.
          </div>
        </>
      )}

      {memberDrill && (
        <MemberTimelineModal
          cboId={cboId}
          memberId={memberDrill}
          onClose={() => setMemberDrill(null)} />
      )}

      {showSchedule && (
        <ScheduleReportModal cboId={cboId}
          defaultName="Weekly Campaign Recap"
          onClose={() => setShowSchedule(false)} />
      )}
    </div>
  );
};

// ============================================================================
// Flow 3 — CampaignEngagementBreakdown (per-step delivery + undelivered reasons)
// ============================================================================
interface EngagementBreakdownRow {
  engagement_order: number;
  engagement_name: string;
  engagement_type: string;
  targeted: number;
  delivered: number;
  undelivered: number;
  responded: number;
  opted_out: number;
  delivery_rate: number;
  undelivered_rate: number;
  undelivered_temporary: number;
  undelivered_permanent: number;
  undelivered_reasons: Record<string, number>;
}

const REASON_LABEL: Record<string, string> = {
  landline: 'landline',
  out_of_range: 'out of range',
  invalid_address: 'invalid address',
  no_answer: 'no answer',
  hard_bounce: 'hard bounce',
  soft_bounce: 'soft bounce',
  voicemail_full: 'voicemail full',
  busy: 'busy',
  unknown: 'unknown',
};

const CampaignEngagementBreakdown: React.FC<{
  cboId: number;
  campaignId: string;
  window: string;
}> = ({ cboId, campaignId, window }) => {
  const [rows, setRows] = useState<EngagementBreakdownRow[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    authFetchJson<EngagementBreakdownRow[]>(
      `/api/campaigns/${campaignId}/engagement-breakdown?cbo_id=${cboId}&window=${window}`,
    )
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [cboId, campaignId, window]);

  if (loading) return <p className="text-sm text-gray-500">Loading engagement breakdown…</p>;
  if (rows.length === 0)
    return <p className="text-sm text-gray-500">No engagement steps configured for this campaign yet.</p>;

  const stepLabel = (r: EngagementBreakdownRow) =>
    `Engagement ${r.engagement_order}${r.engagement_type ? ` · ${r.engagement_type}` : ''}`;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs font-bold uppercase text-gray-500">
          <tr>
            <th className="px-3 py-2">Step</th>
            <th className="px-3 py-2 text-right">Targeted</th>
            <th className="px-3 py-2 text-right">Delivered</th>
            <th className="px-3 py-2 text-right">Delivery %</th>
            <th className="px-3 py-2 text-right">Undelivered</th>
            <th className="px-3 py-2">Undelivered reasons</th>
            <th className="px-3 py-2 text-right">Responded</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const reasonEntries = Object.entries(r.undelivered_reasons || {}).sort(
              (a, b) => Number(b[1]) - Number(a[1]),
            );
            return (
              <tr key={r.engagement_order} className="border-b border-gray-100 align-top">
                <td className="px-3 py-3">
                  <div className="font-semibold text-gray-900">{stepLabel(r)}</div>
                  <div className="text-xs text-gray-500">{r.engagement_name}</div>
                </td>
                <td className="px-3 py-3 text-right tabular-nums">{r.targeted}</td>
                <td className="px-3 py-3 text-right tabular-nums">{r.delivered}</td>
                <td className="px-3 py-3 text-right tabular-nums">
                  {(r.delivery_rate * 100).toFixed(1)}%
                </td>
                <td className="px-3 py-3 text-right tabular-nums">
                  {r.undelivered > 0 ? (
                    <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700">
                      {r.undelivered}
                    </span>
                  ) : (
                    <span className="text-gray-400">0</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  {reasonEntries.length === 0 ? (
                    <span className="text-xs text-gray-400">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {reasonEntries.map(([reason, cnt]) => {
                        const isTemp = ['out_of_range', 'no_answer', 'voicemail_full', 'busy', 'soft_bounce'].includes(reason);
                        return (
                          <span
                            key={reason}
                            title={isTemp ? 'Temporary — will retry on next wave' : 'Permanent failure'}
                            className={`rounded px-1.5 py-0.5 text-[11px] ${
                              isTemp
                                ? 'bg-amber-100 text-amber-900'
                                : 'bg-red-100 text-red-800'
                            }`}>
                            {REASON_LABEL[reason] || reason}: {cnt}
                            {isTemp ? ' · retry' : ''}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-right tabular-nums">{r.responded}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// Flow 3 — CampaignTopMembers (clickable drill-down table)
// ============================================================================
interface TopMemberRow {
  member_id: number;
  first_name: string | null;
  last_name: string | null;
  preferred_language: string | null;
  sent: number;
  delivered: number;
  responded: number;
  opened: number;
  bounced: number;
  opted_out: number;
  last_touch: string | null;
}

const CampaignTopMembers: React.FC<{
  cboId: number;
  campaignId: string;
  window: string;
  onPickMember: (memberId: number) => void;
}> = ({ cboId, campaignId, window, onPickMember }) => {
  const [rows, setRows] = useState<TopMemberRow[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    authFetchJson<TopMemberRow[]>(
      `/api/campaigns/${campaignId}/top-members?cbo_id=${cboId}&window=${window}&limit=20`,
    ).then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, [cboId, campaignId, window]);

  if (loading) return <p className="text-sm text-gray-500">Loading member breakdown…</p>;
  if (rows.length === 0) return <p className="text-sm text-gray-500">No member-level engagement in this window.</p>;

  const langFlag = (l: string | null) =>
    !l ? '' :
    l === 'es' || /spanish/i.test(l) ? '🇲🇽' :
    l === 'vi' || /vietnamese/i.test(l) ? '🇻🇳' :
    l === 'zh' || /chinese/i.test(l) ? '🇨🇳' :
    l === 'ar' || /arabic/i.test(l) ? '🇸🇦' :
    l === 'ko' || /korean/i.test(l) ? '🇰🇷' : '🇺🇸';

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs font-bold uppercase text-gray-500">
          <tr>
            <th className="px-3 py-2">Member</th>
            <th className="px-3 py-2">Lang</th>
            <th className="px-3 py-2 text-right">Sent</th>
            <th className="px-3 py-2 text-right">Delivered</th>
            <th className="px-3 py-2 text-right">Responded</th>
            <th className="px-3 py-2 text-right">Opted out</th>
            <th className="px-3 py-2">Last touch</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.member_id}
              onClick={() => onPickMember(r.member_id)}
              className="cursor-pointer border-b border-gray-100 hover:bg-paper-2">
              <td className="px-3 py-2 font-medium text-blue-700 hover:underline">
                {r.first_name || ''} {r.last_name || `#${r.member_id}`}
              </td>
              <td className="px-3 py-2">{langFlag(r.preferred_language)} <span className="text-xs text-gray-500">{r.preferred_language || ''}</span></td>
              <td className="px-3 py-2 text-right">{r.sent}</td>
              <td className="px-3 py-2 text-right">{r.delivered}</td>
              <td className="px-3 py-2 text-right">{r.responded}</td>
              <td className="px-3 py-2 text-right">
                {r.opted_out > 0
                  ? <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-700">{r.opted_out}</span>
                  : <span className="text-gray-400">—</span>}
              </td>
              <td className="px-3 py-2 text-xs text-gray-500">
                {r.last_touch ? new Date(r.last_touch).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
              </td>
              <td className="px-3 py-2 text-right text-xs text-blue-700">View timeline →</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// Flow 3 — MemberTimelineModal (full per-member engagement history)
// ============================================================================
interface TimelineEvent {
  id?: number | string;
  occurred_at: string;
  event_type: string;
  channel: string | null;
  language: string | null;
  campaign_id?: string | null;
  campaign_name?: string | null;
  template_name?: string | null;
  source_table?: string | null;
  metadata?: any;
}

const EVT_COLOR: Record<string, string> = {
  delivered: 'bg-green-100 text-green-700',
  responded: 'bg-blue-100 text-blue-700',
  opened:    'bg-sky-100 text-sky-700',
  bounced:   'bg-red-100 text-red-700',
  opted_out: 'bg-amber-100 text-amber-900',
};

const MemberTimelineModal: React.FC<{
  cboId: number;
  memberId: number;
  onClose: () => void;
}> = ({ cboId, memberId, onClose }) => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [member, setMember] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    authFetchJson<any>(`/api/members/${memberId}/engagement-timeline?cbo_id=${cboId}&limit=200`)
      .then(d => {
        // Service returns either an array or {events, member, …} depending on shape
        if (Array.isArray(d)) {
          setEvents(d);
        } else {
          setEvents(d.events || []);
          setMember(d.member || null);
        }
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [cboId, memberId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[88vh] w-full max-w-3xl overflow-auto rounded-lg bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">
            Member timeline
            {member && <span className="ml-2 text-sm font-normal text-gray-500">
              · {member.first_name || ''} {member.last_name || `#${memberId}`}
            </span>}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">✕</button>
        </div>
        {loading && <p className="text-sm text-gray-500">Loading timeline…</p>}
        {!loading && events.length === 0 && (
          <p className="text-sm text-gray-500">No engagement events recorded for this member.</p>
        )}
        {!loading && events.length > 0 && (
          <ol className="space-y-2">
            {events.map((e, i) => (
              <li key={e.id ?? i} className="flex items-start gap-3 rounded-lg border border-gray-100 px-3 py-2">
                <div className="w-28 shrink-0 text-xs text-gray-500">
                  {new Date(e.occurred_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
                <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${EVT_COLOR[e.event_type] || 'bg-gray-100 text-gray-700'}`}>
                  {e.event_type}
                </span>
                {e.channel && (
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] uppercase text-gray-600">{e.channel}</span>
                )}
                {e.language && (
                  <span className="rounded bg-paper-2 px-2 py-0.5 text-[10px] uppercase text-ink-500">{e.language}</span>
                )}
                <div className="flex-1 text-sm text-gray-800">
                  {e.campaign_name || e.template_name || e.source_table || '—'}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Flow 1 — UUHPJourneyBuilderPage (full builder: drag + lang + approve + preview)
// ============================================================================
const THRESHOLD_LANGS = ['es', 'vi', 'zh', 'ar', 'ko'];

interface JourneyBuilderCampaign {
  id: string;
  name: string;
  status: string;
  approval_status?: 'not_required' | 'pending_approval' | 'approved' | 'rejected' | null;
  enabled_languages?: string[];
}

export const UUHPJourneyBuilderPage: React.FC<{
  cboId: number;
}> = ({ cboId }) => {
  const [campaigns, setCampaigns] = useState<JourneyBuilderCampaign[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [active, setActive] = useState<JourneyBuilderCampaign | null>(null);
  const [steps, setSteps] = useState<any[]>([]);
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateMsg, setTranslateMsg] = useState<string | null>(null);
  const [bumpKey, setBumpKey] = useState(0);
  // Threshold-language picker — defaults to Spanish (the on-stage language).
  // Narrator toggles Vietnamese / Chinese / Arabic / Korean before clicking
  // '+ Add threshold languages'.
  const [pickedLangs, setPickedLangs] = useState<Set<string>>(new Set(['es']));

  // Load campaigns
  useEffect(() => {
    authFetchJson<any>(`/api/campaigns?cbo_id=${cboId}&limit=50`)
      .then(d => {
        const items: JourneyBuilderCampaign[] = Array.isArray(d) ? d : (d.items || d.campaigns || []);
        setCampaigns(items);
        if (items.length && !activeId) setActiveId(items[0].id);
      })
      .catch(() => setCampaigns([]));
  }, [cboId]);

  // Load campaign + steps when activeId changes
  useEffect(() => {
    if (!activeId) { setActive(null); setSteps([]); return; }
    const meta = campaigns.find(c => c.id === activeId);
    setActive(meta || null);
    authFetchJson<any[]>(`/api/campaigns/${activeId}/engagement-rows?cbo_id=${cboId}`)
      .then(rows => {
        setSteps(rows || []);
        if (rows && rows[0]) setActiveStepId(rows[0].engagement_id);
      })
      .catch(() => setSteps([]));
  }, [activeId, campaigns, bumpKey]);

  const enabledLangs: string[] = active?.enabled_languages?.length
    ? active.enabled_languages
    : ['en'];

  const refreshCampaign = async () => {
    if (!activeId) return;
    // /uuhp-detail returns approval_status + enabled_languages, which the
    // generic /api/campaigns/{id} response model omits. The badge can't
    // update without it.
    const refreshed = await authFetchJson<any>(
      `/api/campaigns/${activeId}/uuhp-detail?cbo_id=${cboId}`,
    ).catch(() => null);
    if (refreshed) {
      setCampaigns(prev => prev.map(c => c.id === activeId ? { ...c, ...refreshed } : c));
      setActive(prev => prev ? { ...prev, ...refreshed } : prev);
    }
  };

  const oneClickTranslate = async () => {
    if (!activeId) return;
    const langs = Array.from(pickedLangs);
    if (langs.length === 0) {
      setTranslateMsg('Pick at least one threshold language to translate.');
      return;
    }
    setTranslating(true); setTranslateMsg(null);
    try {
      const res = await authFetch(`/api/campaigns/${activeId}/translate-steps`, {
        method: 'POST',
        jsonBody: { languages: langs, overwrite: false },
      });
      const j = await res.json().catch(() => ({}));
      const created = j.variants_created ?? j.translations_created ?? j.created ?? 0;
      const stubbed = j.variants_stubbed ?? 0;
      const skipped = j.variants_skipped ?? j.skipped ?? 0;
      const stubNote = stubbed > 0
        ? ` ${stubbed} stubbed (translation provider unavailable — variants flagged for review).`
        : '';
      setTranslateMsg(
        `Translated into ${langs.length} language${langs.length === 1 ? '' : 's'} (${langs.join(', ')}) — ${created} new variants saved${
          skipped ? `, ${skipped} already on file` : ''
        }.${stubNote}`,
      );

      // Update enabled_languages from the response so LanguageVariantTabs
      // immediately shows the new tabs without a full campaigns refetch.
      const newEnabled: string[] | undefined = Array.isArray(j.enabled_languages)
        ? j.enabled_languages
        : undefined;
      if (newEnabled && newEnabled.length > 0) {
        setCampaigns(prev => prev.map(c =>
          c.id === activeId ? { ...c, enabled_languages: newEnabled } : c
        ));
        setActive(prev => prev ? { ...prev, enabled_languages: newEnabled } : prev);
      }

      setBumpKey(k => k + 1);
    } catch (e: any) {
      setTranslateMsg(`Translation failed: ${String(e?.message || e)}`);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-v2border bg-v2accent-soft p-4 text-sm text-v2accent-ink">
        <div className="font-semibold">Build a redetermination campaign · drag-drop journey · multi-language · approval</div>
        <p className="mt-1">
          Pick a campaign, drag steps to reorder them, click <strong>+ Add threshold languages</strong>{' '}
          to one-shot translate every step into Spanish, Vietnamese, Chinese,
          Arabic, and Korean, edit any variant inline, preview the full
          rendered journey, then route through the approval workflow.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-bold uppercase text-gray-500">Campaign</label>
        <select value={activeId || ''} onChange={e => setActiveId(e.target.value || null)}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm">
          {campaigns.length === 0 && <option>(no campaigns)</option>}
          {campaigns.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {active && (
          <span className="text-xs text-gray-500">
            Status: <strong>{active.status}</strong> · Languages: {enabledLangs.join(', ')}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setShowPreview(true)} disabled={!activeId}
            className="rounded bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200 disabled:opacity-50">
            Preview journey
          </button>
          {active && (
            <CampaignApprovalControls
              campaignId={active.id}
              approvalStatus={(active.approval_status || 'not_required') as any}
              onChange={refreshCampaign}
            />
          )}
        </div>
      </div>

      {/* Threshold-language picker — step 1 in the language workflow */}
      {activeId && (
        <div className="rounded-lg border border-v2border bg-v2accent-soft p-3">
          <div className="mb-2 flex items-center gap-2 text-xs">
            <span className="rounded-full bg-v2accent px-2 py-0.5 font-bold uppercase text-white">Step 1</span>
            <span className="font-bold uppercase tracking-wide text-v2accent-ink">Pick threshold languages to add</span>
            <span className="ml-auto text-v2accent-ink">Sofia speaks <strong>Spanish</strong> — default selection</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {THRESHOLD_LANGS.map(l => {
              const checked = pickedLangs.has(l);
              return (
                <button
                  key={l}
                  type="button"
                  onClick={() => {
                    setPickedLangs(prev => {
                      const next = new Set(prev);
                      if (next.has(l)) next.delete(l); else next.add(l);
                      return next;
                    });
                  }}
                  className={`flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition ${
                    checked
                      ? 'border-v2accent bg-v2accent text-white'
                      : 'border-v2border bg-surface text-ink-500 hover:text-ink'
                  }`}>
                  <span>{LANG_FLAG[l] || '🌐'}</span>
                  <span className="font-mono uppercase">{l}</span>
                  {checked && <span>✓</span>}
                </button>
              );
            })}
            <span className="ml-auto text-xs text-gray-500">
              {pickedLangs.size} of {THRESHOLD_LANGS.length} selected
            </span>
            <button onClick={oneClickTranslate}
              disabled={translating || !activeId || pickedLangs.size === 0}
              className="rounded bg-v2accent px-3 py-1.5 text-sm font-medium text-white hover:bg-v2accent/90 disabled:opacity-50">
              {translating ? 'Translating…' : `+ Add ${pickedLangs.size} threshold language${pickedLangs.size === 1 ? '' : 's'}`}
            </button>
          </div>
          <div className="mt-2 text-[11px] text-purple-800">
            <strong>Step 2:</strong> click <em>+ Add …</em>. The journey-translation
            service writes a variant row per step per language. Seeded
            campaigns ship with curated translations; new campaigns fall back
            to Google Translate when the API key is configured.
          </div>
        </div>
      )}

      {translateMsg && (
        <div className="rounded-lg border border-v2border bg-v2accent-soft p-3 text-xs text-v2accent-ink">
          {translateMsg}
        </div>
      )}

      {activeId && (
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">
              Journey steps · drag to reorder
            </h3>
            <JourneyStepDragDrop campaignId={activeId} cboId={cboId} onSaved={() => setBumpKey(k => k + 1)} />
          </div>
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">
                Step content · all languages side-by-side
              </h3>
              {steps.length > 0 && (
                <select value={activeStepId || ''} onChange={e => setActiveStepId(e.target.value)}
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm">
                  {steps.map((s: any, i: number) => (
                    <option key={s.engagement_id} value={s.engagement_id}>
                      Step {i + 1} · {s.name || s.engagement_type}
                    </option>
                  ))}
                </select>
              )}
              <span className="ml-auto text-xs text-gray-500">
                {enabledLangs.length} language{enabledLangs.length === 1 ? '' : 's'} enabled
              </span>
            </div>
            {steps.length > 0 ? (
              <JourneyStepLanguageGrid
                campaignId={activeId}
                step={steps.find((s: any) => s.engagement_id === activeStepId) || steps[0]}
                enabledLanguages={enabledLangs}
                onSaved={() => setBumpKey(k => k + 1)}
              />
            ) : (
              <p className="text-sm text-gray-500">No steps in this journey yet.</p>
            )}
          </div>
        </div>
      )}

      {showPreview && activeId && (
        <JourneyPreviewModal
          campaignId={activeId}
          enabledLanguages={enabledLangs}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};
